import {
  INTENT_GRID_DETAILS,
  CONTENT_QUALITY_TBL,
  PAGE_SOURCE,
  CR_TAB_KEYS,
} from '../constants/content-recommendations';
import {
  checkValueExists,
  extractCompetitorName,
  getTagGroupKey,
  getTagLabel,
  numberFormatter,
  sortArrayByKey,
  isValidScore,
  _RESOLUTION_ENTITY_TYPE,
} from './common';
import CRKeywordOpportunity from '../components/content-recommendations/CRListComponent/Component/CRKeywordOpportunity';
import ApiService from '../api/restApi';
import { isArray } from 'lodash';
import { APP_STORE } from './store/redux';
import QwaPublicAPIAccess from '../api/QwaPublicAPIAccess';
import { format } from 'date-fns';
import { typeKeywordDetails } from '../components/content-recommendations/Content-Brief/contentBriefType';
import { ANNOTATION_TYPE } from '../constants/formMeta/recommendation';
import { trackAnalyticsEvent } from '../constants/analyticsMeta';
import { setCookie } from '../components/content-ai-tools/components/tools-common';
import { signOut } from './services/authActions';
import { ContactSupportOutlined } from '@material-ui/icons';
import { streamRewriteTextBody } from '../components/content-recommendations/contentEditorPage/AutoWrite-Modals/rewriteGptModel';

const ONLY_HEADINGS_CHILD_TAGS = ['P', 'PRE', 'LI', 'LABEL', 'SPAN']; //HERE we are skipping existing elements headings from parent headings

export const getRelatedAndSuggestedIncludingString = ({
  customerHtmlData,
  competitorHtmlData,
  selectedImproveKeyword,
  storedKeywordsData,
  textValue,
  relatedTerms,
  synonyms,
  edit = false,
  isCustomer,
}) => {
  const { originalRelatedTerm = [], originalSynonyms = [] } = isCustomer
    ? setRelatedTermSynonymsToState({
        selectedKeyword: selectedImproveKeyword,
        keywordsData: storedKeywordsData,
        tableData: customerHtmlData,
      })
    : {};
  const selectedKeyword = selectedImproveKeyword;
  const encryptedSynonyms = getEncryptedSynonyms({ textValue, selectedSynonyms: synonyms, originalSynonyms });
  const encryptedRelatedTerms = getEncryptedRelatedHighlightedKeywords({
    columnValue: encryptedSynonyms.text,
    relatedTerms,
    originalRelatedTerm,
  });
  const encryptedKeywords = getHighlightedKeywords({
    columnValue: encryptedRelatedTerms.text,
    showCount: !edit,
    selectedKeyword,
  });
  const decryptedSynonyms = getDecryptedSynonyms({
    columnValue: encryptedKeywords.text,
    showCount: !edit,
    isCustomer: isCustomer,
    originalSynonyms,
    customerData: customerHtmlData,
    competitorData: competitorHtmlData,
    listOfOriginalWords: encryptedSynonyms.listOfOriginalWords,
  });
  const decryptedRelatedTerms = getDecryptedRelatedHighlightedKeywords({
    columnValue: decryptedSynonyms,
    showCount: !edit,
    isCustomer: isCustomer,
    originalRelatedTerm,
    customerData: customerHtmlData,
    competitorData: competitorHtmlData,
    listOfOriginalWords: encryptedRelatedTerms.listOfOriginalWords,
  });
  const decryptedKeywords = getDecryptedHighlightedKeywords({
    columnValue: decryptedRelatedTerms,
    showCount: !edit,
    isCustomer: isCustomer,
    selectedKeyword,
    customerData: customerHtmlData,
    competitorData: competitorHtmlData,
    listOfOriginalWords: encryptedKeywords.listOfOriginalWords,
  });
  return decryptedKeywords;
};

export const getEncryptedSynonyms = ({ textValue, selectedSynonyms, originalSynonyms }) => {
  let text = `${textValue}`;
  const listOfOriginalWords = {};
  if (text && selectedSynonyms.length) {
    for (let i = 0; i < selectedSynonyms.length; i++) {
      const regEx = new RegExp(selectedSynonyms[i], 'ig');
      const originalTexts = textValue.match(regEx);
      const index = originalSynonyms.findIndex((o) => o.term == selectedSynonyms[i]);
      if (index !== -1) {
        listOfOriginalWords[i] = originalTexts;
        const replaceMask = `~~##${index}~~##`;
        text = text.replace(regEx, replaceMask);
      }
    }
  }
  return { text, listOfOriginalWords };
};

export const getEncryptedRelatedHighlightedKeywords = ({ columnValue, relatedTerms, originalRelatedTerm }) => {
  let text = `${columnValue}`;
  const listOfOriginalWords = {};
  if (text && relatedTerms.length) {
    for (let i = 0; i < relatedTerms.length; i++) {
      const regEx = new RegExp(relatedTerms[i], 'ig');
      const originalTexts = columnValue.match(regEx);
      const index = originalRelatedTerm.findIndex((o) => o.term == relatedTerms[i]);
      if (index !== -1) {
        listOfOriginalWords[i] = originalTexts;
        const replaceMask = `~#${index}~#`;
        text = text.replace(regEx, replaceMask);
      }
    }
  }
  return { text, listOfOriginalWords };
};

/**
 *
 * @param {{columnValue: string, showCount: boolean, selectedKeyword: string}} options
 * @returns {{ text: string, listOfOriginalWords: Record<number, string>, originalStrings: undefined}}
 */
export const getHighlightedKeywords = ({ columnValue, showCount = true, selectedKeyword }) => {
  let text = `${columnValue}`;
  let counter = 0;

  const highlightKeywords = selectedKeyword ? [selectedKeyword] : [];
  const listOfOriginalWords = {};
  if (text) {
    for (let i = 0; i < highlightKeywords.length; i++) {
      const regEx = new RegExp(highlightKeywords[i], 'gi'); // old is "gi" new is "ig"
      const originalTexts = columnValue.match(regEx);
      const replaceMask = `~##${i}##~`;
      text = text.replace(regEx, replaceMask);
      listOfOriginalWords[i] = originalTexts;
    }
  }

  if (counter && showCount) {
    return { text: `${text}<sup class="highlight-keyword-count">${counter}</sup>`, originalStrings: undefined };
  } else {
    return { text, listOfOriginalWords };
  }
};

export const getDecryptedSynonyms = ({
  columnValue,
  showCount = true,
  isCustomer,
  originalSynonyms,
  customerData = [] as any[],
  competitorData = [] as any[],
  listOfOriginalWords = {},
}) => {
  let text = `${columnValue}`;
  if (text && originalSynonyms.length) {
    for (let i = 0; i < originalSynonyms.length; i++) {
      let counter = 0;
      const regEx = new RegExp(`~~##${i}~~##`, 'ig');
      let foundKeyword = text.match(regEx);

      let replaceMask = `<span class=synonym-keyword>${originalSynonyms[i].term}</span>`;
      if (foundKeyword && foundKeyword.length) {
        if (isCustomer) {
          customerData.forEach((data) => {
            const regExToMatchKeywords = new RegExp(`${originalSynonyms[i].term}`, 'ig');
            let occurrences = data.text.match(regExToMatchKeywords);
            if (occurrences && occurrences.length) {
              counter = counter + occurrences.length;
            }
          });
        } else {
          competitorData.forEach((data) => {
            const regExToMatchKeywords = new RegExp(`${originalSynonyms[i].term}`, 'ig');
            let occurrences = data.text.match(regExToMatchKeywords);
            if (occurrences && occurrences.length) {
              counter = counter + occurrences.length;
            }
          });
        }
        const singleExp = new RegExp(`~~##${i}~~##`, 'i');
        foundKeyword.forEach((word, index) => {
          const original = listOfOriginalWords
            ? listOfOriginalWords[i] && listOfOriginalWords[i][index]
              ? listOfOriginalWords[i][index]
              : null
            : null;
          const string = original ? original : originalSynonyms[i].term;
          if (counter && showCount) {
            replaceMask = `<span class=related-highlighted-keyword>${string}</span><sup class="related-highlighted-keyword-count">+${counter}</sup>`;
          } else {
            replaceMask = `<span class=related-highlighted-keyword>${string}</span>`;
          }
          text = text.replace(singleExp, replaceMask);
        });
      }
    }
  }
  return `${text}`;
};

export const getDecryptedRelatedHighlightedKeywords = ({
  columnValue,
  showCount = true,
  isCustomer = true,
  originalRelatedTerm,
  customerData = [] as any[],
  competitorData = [] as any[],
  listOfOriginalWords = {},
}) => {
  let text = `${columnValue}`;
  if (text && originalRelatedTerm.length) {
    for (let i = 0; i < originalRelatedTerm.length; i++) {
      let counter = 0;

      const regEx = new RegExp(`~#${i}~#`, 'ig');
      let foundKeyword = text.match(regEx);

      let replaceMask = `<span class=related-highlighted-keyword>${originalRelatedTerm[i].term}</span>`;
      if (foundKeyword && foundKeyword.length) {
        if (isCustomer) {
          customerData.forEach((data) => {
            const regExToMatchKeywords = new RegExp(`${originalRelatedTerm[i].term}`, 'ig');
            // @ts-expect-error legacy code. Should likely just be `data.text.match(regexToMatchKeywords)`
            let occurrences = data.text.match(eval(regExToMatchKeywords));
            if (occurrences && occurrences.length) {
              counter = counter + occurrences.length;
            }
          });
        } else {
          competitorData.forEach((data) => {
            const regExToMatchKeywords = new RegExp(`${originalRelatedTerm[i].term}`, 'ig');
            let occurrences = data.text.match(regExToMatchKeywords);
            if (occurrences && occurrences.length) {
              counter = counter + occurrences.length;
            }
          });
        }
        const singleExp = new RegExp(`~#${i}~#`, 'i');
        foundKeyword.forEach((word, index) => {
          const original = listOfOriginalWords
            ? listOfOriginalWords[i] && listOfOriginalWords[i][index]
              ? listOfOriginalWords[i][index]
              : null
            : null;
          const string = original ? original : originalRelatedTerm[i].term;
          if (counter && showCount) {
            replaceMask = `<span class=related-highlighted-keyword>${string}</span><sup class="related-highlighted-keyword-count">+${counter}</sup>`;
          } else {
            replaceMask = `<span class=related-highlighted-keyword>${string}</span>`;
          }
          text = text.replace(singleExp, replaceMask);
        });
      }
    }
  }
  return `${text}`;
};

export const getDecryptedHighlightedKeywords = ({
  columnValue,
  showCount = true,
  isCustomer = true,
  selectedKeyword,
  customerData = [] as any[],
  competitorData = [] as any[],
  listOfOriginalWords = {},
}) => {
  let text = `${columnValue}`;
  let counter = 0;
  const highlightKeywords = selectedKeyword ? [selectedKeyword] : [];
  if (text) {
    for (let i = 0; i < highlightKeywords.length; i++) {
      const regEx = new RegExp(`~##${i}##~`, 'ig');
      let foundKeyword = text.match(regEx);
      let replaceMask = `<span class=highlight-keyword>${highlightKeywords[i]}</span>`;
      if (foundKeyword && foundKeyword.length) {
        if (isCustomer) {
          customerData.forEach((data) => {
            const regExToMatchKeywords = new RegExp(`${highlightKeywords[i]}`, 'ig');
            let occurrence = data.text.match(regExToMatchKeywords);
            if (occurrence && occurrence.length) {
              counter = counter + occurrence.length;
            }
          });
        } else {
          competitorData.forEach((data) => {
            const regExToMatchKeywords = new RegExp(`${highlightKeywords[i]}`, 'ig');
            let occurrence = data.text.match(regExToMatchKeywords);
            if (occurrence && occurrence.length) {
              counter = counter + occurrence.length;
            }
          });
        }
        const singleExp = new RegExp(`~##${i}##~`, 'i');
        foundKeyword.forEach((word, index) => {
          const original = listOfOriginalWords
            ? listOfOriginalWords[i] && listOfOriginalWords[i][index]
              ? listOfOriginalWords[i][index]
              : null
            : null;
          const string = original ? original : highlightKeywords[i].term;
          if (counter && showCount) {
            replaceMask = `<span class=related-highlighted-keyword>${string}</span><sup class="related-highlighted-keyword-count">+${counter}</sup>`;
          } else {
            replaceMask = `<span class=related-highlighted-keyword>${string}</span>`;
          }
          text = text.replace(singleExp, replaceMask);
        });
      }
      text = text.replace(regEx, replaceMask);
    }
  }
  return text;
};

export const setRelatedTermSynonymsToState = ({ selectedKeyword, keywordsData, tableData }) => {
  let matchedKeywords = selectedKeyword ? [selectedKeyword] : [];

  let relatedKeywords = [] as any[],
    isDataFound = false;
  let synonymsKeywords = [] as any[],
    synonymsCount;
  keywordsData?.forEach((keywordData) => {
    if (
      !isDataFound &&
      (matchedKeywords.includes(keywordData.keyword) || matchedKeywords.includes(keywordData.sub_keyword)) &&
      keywordData &&
      keywordData.keyword_related_terms
    ) {
      let parsed = safelyParse(keywordData.keyword_related_terms)
        ? safelyParse(keywordData.keyword_related_terms) instanceof Array
          ? safelyParse(keywordData.keyword_related_terms)
          : []
        : [];
      if (parsed && parsed.length) {
        isDataFound = true;
        parsed.forEach((word) => {
          if (!relatedKeywords.some((o) => o.term == word.KEYWORD)) {
            let occurancesCount = 0;
            tableData.forEach((dataObj) => {
              if (dataObj.text && dataObj.text.toLowerCase().includes(word.KEYWORD.toLowerCase().trim())) {
                occurancesCount = occurancesCount + 1;
              }
            });
            relatedKeywords.push({ term: word.KEYWORD, score: word.KEYWORD_IMPORTANCE, occurances: occurancesCount });
          }
        });
      }
    }
    if (
      (matchedKeywords.includes(keywordData.keyword) || matchedKeywords.includes(keywordData.sub_keyword)) &&
      keywordData &&
      keywordData.comp_extracted_keywords
    ) {
      let parsed = safelyParse(keywordData.comp_extracted_keywords)
        ? safelyParse(keywordData.comp_extracted_keywords) instanceof Array
          ? safelyParse(keywordData.comp_extracted_keywords)
          : []
        : [];
      if (parsed && parsed.length) {
        parsed.forEach((word) => {
          if (!synonymsKeywords.some((o) => o.term == word.KEYWORD)) {
            synonymsCount = 0;
            tableData.forEach((dataObj) => {
              if (dataObj.text && dataObj.text.includes(word.KEYWORD)) synonymsCount++;
            });
            synonymsKeywords.push({ term: word.KEYWORD, score: word.KEYWORD_IMPORTANCE, occurances: synonymsCount });
          }
        });
      }
    }
  });
  let sortedOriginalRelatedTerm = [...relatedKeywords];
  sortedOriginalRelatedTerm = sortedOriginalRelatedTerm.sort((a, b) =>
    a.score > b.score ? -1 : b.score > a.score ? 1 : 0
  );
  let sortedSynonyms = [...synonymsKeywords];
  sortedSynonyms = sortedSynonyms.sort((a, b) => (a.score > b.score ? -1 : b.score > a.score ? 1 : 0));
  return {
    originalRelatedTerm: sortedOriginalRelatedTerm.slice(0, 20),
    originalSynonyms: sortedSynonyms.slice(0, 20),
  };
};

export const getTabLabelDetails = (tag) => {
  let updatedTag = tag.toLowerCase();
  switch (updatedTag) {
    case 'a':
    case 'A':
      return { label: 'Anchor Tag - <a>', tagGroup: 'Links', shortLabel: 'Anchor Tag', justTag: 'Link' };

    case 'H1':
    case 'h1':
      return { label: 'Heading - <h1>', tagGroup: 'Meta', shortLabel: 'Heading', justTag: 'H1' };

    case 'H2':
    case 'h2':
      return { label: 'Heading - <h2>', tagGroup: 'Headings', shortLabel: 'Heading', justTag: 'H2' };

    case 'H3':
    case 'h3':
      return { label: 'Heading - <h3>', tagGroup: 'Headings', shortLabel: 'Heading', justTag: 'H3' };

    case 'H4':
    case 'h4':
      return { label: 'Heading - <h4>', tagGroup: 'Headings', shortLabel: 'Heading', justTag: 'H4' };

    case 'H5':
    case 'h5':
      return { label: 'Heading - <h5>', tagGroup: 'Headings', shortLabel: 'Heading', justTag: 'H5' };

    case 'H6':
    case 'h6':
      return { label: 'Heading - <h6>', tagGroup: 'Headings', shortLabel: 'Heading', justTag: 'H6' };

    case 'IMG':
    case 'img':
      return { label: 'Image - <img>', tagGroup: 'Images', shortLabel: 'Image', justTag: 'Image' };

    case 'P':
    case 'p':
      return { label: 'Paragraph - <p>', tagGroup: 'Body', shortLabel: 'Paragraph', justTag: 'P' };

    case 'DIV':
    case 'div':
      return { label: 'Divider - <div>', tagGroup: 'Body', shortLabel: 'Div', justTag: 'Div' };

    case 'UL':
    case 'ul':
      return { label: 'Unordered List - <ul>', tagGroup: 'Body', shortLabel: 'Unordered List', justTag: 'UL' };

    case 'LI':
    case 'li':
      return { label: 'Ordered List - <li>', tagGroup: 'Body', shortLabel: 'Ordered List', justTag: 'LI' };

    case 'SPAN':
    case 'span':
      return { label: 'Span - <span>', tagGroup: 'Body', shortLabel: 'Span', justTag: 'Span' };

    case 'PRE':
    case 'pre':
      return { label: 'Preformatted - <pre>', tagGroup: 'Body', shortLabel: 'Pre', justTag: 'Pre' };

    case 'TITLE':
    case 'title':
      return { label: tag, tagGroup: 'Meta', shortLabel: tag };
    case 'DESCRIPTION':
    case 'description':
    case 'meta description':
      return { label: tag, tagGroup: 'Meta' };

    default:
      return { label: tag, tagGroup: tag };
  }
};

export const mapParameterName = (param) => {
  switch (param) {
    case 'h1':
      return 'H1';
    case 'h2':
      return 'H2';
    case 'h3':
      return 'H3';
    case 'h4':
      return 'H4';
    case 'h5':
      return 'H5';
    case 'h6':
      return 'H6';
    case 'title':
      return 'Title';
    case 'description':
      return 'Meta Description';
    case 'images':
      return 'IMG';
    case 'links':
      return 'a';
    default:
      return param;
  }
};

export const getTextKeysAndData = (obj) => {
  if (obj.modified_data) {
    return {
      text: obj.modified_data.value,
      [`text_original`]: !obj.text ? '' : obj.text,
      id: obj.id,
      relatedTerms: obj.modified_data.keywords ? JSON.parse(obj.modified_data.keywords) : [],
      synonyms: obj.modified_data.synonyms ? JSON.parse(obj.modified_data.synonyms) : [],
      isSuggested: obj.isSuggested ? obj.isSuggested : false,
      created_by: obj.created_by ? obj.created_by : null,
      created_on: obj.created_on ? obj.created_on : null,
      updated_by: obj.updated_by ? obj.updated_by : null,
      updated_on: obj.updated_on ? obj.updated_on : null,
      siblings: obj.siblings,
      nestedSiblings: obj?.nestedSiblings || [],
      is_duplicate: obj.is_duplicate,
      newLink: obj.modified_data.url || obj.modified_data.link || obj.modified_data.href || obj.modified_data.src,
      elementOrderNumber: obj?.elementOrderNumber,
      html_original: obj?.modified_data?.originalHtml,
      html: obj?.modified_data?.html,
    };
  } else if (obj.isSuggested) {
    return {
      text: obj.data.value,
      id: obj.id,
      relatedTerms: obj.data.keywords ? safelyParse(obj.data.keywords) : [],
      synonyms: obj.data.synonyms ? safelyParse(obj.data.synonyms) : [],
      isSuggested: obj.isSuggested ? obj.isSuggested : false,
      created_by: obj.created_by ? obj.created_by : null,
      created_on: obj.created_on ? obj.created_on : null,
      updated_by: obj.updated_by ? obj.updated_by : null,
      updated_on: obj.updated_on ? obj.updated_on : null,
      nestedSiblings: obj?.nestedSiblings || [],
      siblings: obj.siblings,
      is_duplicate: obj.is_duplicate,
      elementOrderNumber: obj?.elementOrderNumber,
    };
  } else {
    return {
      text: !obj.text ? '' : obj.text,
      id: obj.id,
      isSuggested: obj.isSuggested ? obj.isSuggested : false,
      updated_by: obj.updated_by ? obj.updated_by : null,
      updated_on: obj.updated_on ? obj.updated_on : null,
      siblings: obj.siblings,
      nestedSiblings: obj?.nestedSiblings || [],
      is_duplicate: obj.is_duplicate,
      elementOrderNumber: obj?.elementOrderNumber,
    };
  }
};

export const safelyParse = (data, requiredArray = false) => {
  let newData = data;
  if (!data) {
    return data;
  }
  try {
    newData = JSON.parse(data);
  } catch (e) {
    if (requiredArray && !isArray(data)) {
      newData = [];
    } else {
      newData = data;
    }
  }
  return newData;
};

// GIGA accepts before the in-place rescore fix (2026-06-11) inserted a
// suggested html_data copy for every KR-rewritten EXISTING element; the
// backend now updates the element's own row instead, but rows inserted by
// older accepts remain in the DB. When a suggested row shares its element id
// with a real (non-suggested) row in the same tab, it is that legacy
// duplicate — rendering both shows an Improved + Proposed pair for one
// element. Keep the real row (it carries the live text + edit affordances),
// drop the copy. Genuine GIGA additions keep ids that no live row has, so
// they are unaffected.
export const dropSuggestedCopiesOfExistingElements = (rows: any[]) => {
  if (!Array.isArray(rows) || !rows.length) return rows;
  const realIds = new Set(rows.filter((row) => !row?.isSuggested && row?.id != null).map((row) => String(row.id)));
  if (!realIds.size) return rows;
  return rows.filter((row) => !(row?.isSuggested && row?.id != null && realIds.has(String(row.id))));
};

export const formatAllTabData = (respData) => {
  ////NOTE if any new tag group is added or changed pleas check dependencies in getParamName() and update it
  let metaTabData = [] as any[],
    headingsTabData = [] as any[],
    linksTabData = [] as any[],
    imagesTabData = [] as any[],
    jsonldData = [] as any[],
    bodyTabData = [] as any[];
  metaTabData = [...respData.title, ...respData.h1, ...respData.description];
  metaTabData = metaTabData.map((obj, index) => {
    return {
      tag: mapParameterName(obj.param),
      ...getTextKeysAndData(obj),
      eleIndex: obj.isSuggested ? '' : obj.param === 'h1' ? index + 1 : '',
    };
  });

  headingsTabData = [...respData.h2, ...respData.h3, ...respData.h4, ...respData.h5, ...respData.h6];

  headingsTabData = headingsTabData.map((obj, index) => {
    return {
      tag: mapParameterName(obj.param),
      ...getTextKeysAndData(obj),
      eleIndex: obj.isSuggested ? '' : index + 1,
    };
  });

  linksTabData = respData.links.map((obj, index) => {
    return {
      tag: mapParameterName(obj.param),
      link: obj.url,
      ...getTextKeysAndData(obj),
      eleIndex: '',
      // eleIndex: obj.isSuggested ? '' : (index +1)
    };
  });

  imagesTabData = respData.images.map((obj, index) => {
    return {
      tag: mapParameterName(obj.param),
      link: obj.url,
      ...getTextKeysAndData(obj),
      eleIndex: '',
      isOgImage: (obj as any).isOgImage || false,
      width: (obj as any).width ?? null,
      height: (obj as any).height ?? null,
      // eleIndex: obj.isSuggested ? '' : (index +1)
    };
  });

  jsonldData =
    (respData.jsonld &&
      respData.jsonld.map((obj) => {
        return {
          tag:
            typeof obj.markup_attribute == 'string'
              ? obj.markup_attribute
              : obj.markup_attribute instanceof Array
              ? obj.markup_attribute[0]
              : '',
          link: obj.structured_data,
          col4: obj.text,
          parent_type: obj.parent_type,
          rating: obj.rating,
          html_tag: 'JSONLD',
          // @ts-expect-error legacy code
          ...getTextKeysAndData(obj, 'col3'),
        };
      })) ||
    [];

  const bodyTagsToRead = [...respData.pre, ...respData.li, ...respData.label];
  const spansList = respData?.span || [];
  bodyTabData = [...respData.p, ...spansList, ...bodyTagsToRead];

  bodyTabData = bodyTabData.map((obj, index) => {
    return {
      tag: mapParameterName(obj.param),
      ...getTextKeysAndData(obj),
      eleIndex: obj.isSuggested ? '' : index + 1,
    };
  });

  metaTabData = dropSuggestedCopiesOfExistingElements(metaTabData);
  headingsTabData = dropSuggestedCopiesOfExistingElements(headingsTabData);
  linksTabData = dropSuggestedCopiesOfExistingElements(linksTabData);
  imagesTabData = dropSuggestedCopiesOfExistingElements(imagesTabData);
  bodyTabData = dropSuggestedCopiesOfExistingElements(bodyTabData);

  const tabData = {
    META: metaTabData && metaTabData.filter((k) => !k.is_duplicate),
    HEADINGS: headingsTabData && headingsTabData.filter((k) => !k.is_duplicate),
    LINKS: linksTabData && linksTabData.filter((k) => !k.is_duplicate),
    IMAGES: imagesTabData && imagesTabData.filter((k) => !k.is_duplicate),
    'STRUCTURED DATA': jsonldData && jsonldData.filter((k) => !k.is_duplicate),
    BODY: bodyTabData && bodyTabData.filter((k) => !k.is_duplicate),
    'ALL TOPICS': [],
    s3_key: respData.s3_key,
    created_at: respData.created_at,
  };
  const allHTMLData = [
    ...metaTabData,
    ...headingsTabData,
    ...linksTabData,
    ...imagesTabData,
    ...jsonldData,
    ...bodyTabData,
  ];

  return {
    tabData,
    allHTMLData,
  };
};

export const getContentHtml = (
  contentData: any[],
  topicsAllHtmlData,
  topicsCompetitorHtmlData,
  selectCompetitorFrom = [] as any[]
) => {
  const improveHeadingData = [] as any[],
    customerHtmlContent = [] as any[],
    competitorHtmlContent = [] as any[];
  let matchedRecord, dataObj;
  let topicLevelUpdatedInfo = {
    isEdited: false,
    isSuggested: false,
    updated_by: '',
    updated_on: '',
    created_on: '',
  };
  let childIsUpdated = false;
  contentData.forEach((obj) => {
    if (obj.flag == 0) {
      matchedRecord = topicsCompetitorHtmlData.find((k) => k.id == obj.html_id);
      dataObj = {
        ...obj,
        isCustomer: false,
        name: matchedRecord && matchedRecord.text ? matchedRecord.text : obj.name,
      };
      if (selectCompetitorFrom.length === 0 || selectCompetitorFrom.includes(obj.url)) {
        improveHeadingData.push({ ...obj, isCustomer: false });
      }
      competitorHtmlContent.push(dataObj);
    } else {
      matchedRecord = topicsAllHtmlData.find((k) => k.id == obj.html_id);
      if (matchedRecord && matchedRecord?.siblings?.length) {
        matchedRecord.siblings.forEach((siblingData) => {
          const matchedSiblingRecord = topicsAllHtmlData.find(
            (k) => k.id == siblingData.id && ONLY_HEADINGS_CHILD_TAGS.includes(siblingData.tagName.toUpperCase())
          );
          if (matchedSiblingRecord) {
            const siblingsUpdatedInfo = {
              isSuggested: matchedSiblingRecord?.isSuggested,
              created_on: matchedSiblingRecord?.created_on,
              updated_on: matchedSiblingRecord?.updated_on,
              created_by: matchedSiblingRecord?.created_by,
              updated_by: matchedSiblingRecord?.updated_by,
              isEdited: Boolean(matchedSiblingRecord?.text_original),
            };
            if (
              topicLevelUpdatedInfo.updated_on &&
              (siblingsUpdatedInfo.created_on || siblingsUpdatedInfo.updated_on)
            ) {
              if (new Date(siblingsUpdatedInfo.updated_on) > new Date(topicLevelUpdatedInfo.updated_on)) {
                topicLevelUpdatedInfo = { ...siblingsUpdatedInfo, isEdited: true, isSuggested: false };
              }
            } else if (siblingsUpdatedInfo.created_on || siblingsUpdatedInfo.updated_on) {
              topicLevelUpdatedInfo = { ...siblingsUpdatedInfo, isEdited: true, isSuggested: false };
            }
          }
        });
      }
      const updatedInfo = {
        isSuggested: matchedRecord?.isSuggested,
        created_on: matchedRecord?.created_on,
        updated_on: matchedRecord?.updated_on,
        updated_by: matchedRecord?.updated_by,
        created_by: matchedRecord?.created_by,
        isEdited: Boolean(matchedRecord?.text_original),
      };
      if (topicLevelUpdatedInfo.updated_on && (updatedInfo.created_on || updatedInfo.updated_on)) {
        if (new Date(updatedInfo.updated_on) > new Date(topicLevelUpdatedInfo.updated_on)) {
          topicLevelUpdatedInfo = { ...updatedInfo, isEdited: true, isSuggested: false };
        }
      } else if (updatedInfo.created_on || updatedInfo.updated_on) {
        topicLevelUpdatedInfo = { ...updatedInfo, isEdited: true, isSuggested: false };
      }
      dataObj = {
        ...obj,
        isCustomer: true,
        name: matchedRecord && matchedRecord.text ? matchedRecord.text : obj.name,
        updatedInfo,
      };
      improveHeadingData.push(dataObj);
      customerHtmlContent.push(dataObj);
    }
  });
  return { improveHeadingData, customerHtmlContent, competitorHtmlContent, topicLevelUpdatedInfo };
};
export const getUpdatedTextAndData = ({
  currentText,
  selectedWord,
  tempData = [] as any[],
  carretIndex = currentText.length + 1,
}) => {
  // @ts-expect-error legacy code. we should remove this ASAP, modifying an object's `prototype` is NOT good
  String.prototype.splice = function (idx, rem, str) {
    return this.slice(0, idx) + str + this.slice(idx + Math.abs(rem));
  };
  let updatedData = [...tempData];
  let updatedText = currentText;
  if (tempData.includes(selectedWord)) {
    let index = tempData.indexOf(selectedWord);
    updatedData.splice(index, 1);
    updatedText = currentText.replace(`${selectedWord}`, '');
    updatedText = updatedText.replace(/\s+$/, ''); // To remove last space from string
  } else {
    updatedData.push(selectedWord);
    updatedText = currentText.splice(carretIndex, 0, ` ${selectedWord}`);
  }
  return {
    updatedText,
    updatedData,
  };
};
export const getUpdatedRuleText = ({ currentText, selectedWord, carretIndex = currentText.length + 1 }) => {
  // @ts-expect-error legacy code. we should remove this ASAP, modifying an object's `prototype` is NOT good
  String.prototype.splice = function (idx, rem, str) {
    return this.slice(0, idx) + str + this.slice(idx + Math.abs(rem));
  };
  let updatedText = currentText;
  updatedText = currentText.splice(carretIndex, 0, ` ${selectedWord} `);
  return {
    updatedText,
  };
};

export const getKSMPayload = ({
  pageUrl,
  tag,
  updatedText,
  id,
  tab,
  keywordsData = [],
  customerAllHtmlData = [],
  customerLoginInfo,
}) => {
  // const { keywords, keywordList } = getKeywords(keywordsData);
  // let { kes, kis, tag_group_score, tag_scores } = getKeywordScore(keywordsData, keywords, tab);
  const fakeElement: any = [
    {
      id: 1,
      score: 0,
      tag: 'P',
    },
  ];
  const keywordPayloadData = getKSMV2Data(keywordsData, customerAllHtmlData, fakeElement);
  let htmlElmentPayloadData = getHtmlElementsKSMV2(customerAllHtmlData);
  if (!htmlElmentPayloadData?.length) {
    htmlElmentPayloadData = [
      {
        element_id: 1,
        corpus: '',
        is_duplicate: false,
      },
    ];
  }
  const ksmapiversion =
    customerLoginInfo && customerLoginInfo.additional_user_info && customerLoginInfo.additional_user_info.ksmapiversion
      ? customerLoginInfo.additional_user_info.ksmapiversion
      : process.env.NEXT_PUBLIC_DEFAULT_KSM_API_VERSION;

  return {
    ksm_api_version: ksmapiversion,
    finalurl: pageUrl,
    tag:
      tag == 'a'
        ? 'LINKS'
        : tag == 'IMG'
        ? 'IMAGES'
        : tag == 'Meta Description'
        ? 'DESCRIPTION'
        : tab == 'STRUCTURED DATA'
        ? 'JSONLD'
        : tag.toUpperCase(),
    tag_group: getTagGroupKey(tab),
    corpus: updatedText,
    element_id: Number(id),
    html_element: htmlElmentPayloadData,
    keywords: keywordPayloadData,
  };
};

const mapAllHtmlDataAndTopicContent = ({ allHtmlData, allTopicData }) => {
  console.log('allHtmlData', allHtmlData);
  const updatedTopicData = [] as any[];
  allTopicData.forEach((tData) => {
    const topicDataDetails = { ...tData, CONTENT: [] };
    tData.CONTENT.forEach((tDataContent) => {
      const updatedTopiContent = { ...tDataContent, children: [] };
      const foundObj = allHtmlData.find((allData) => allData.id == tDataContent.html_id);
      if (foundObj) {
        foundObj.siblings &&
          foundObj.siblings.length &&
          foundObj.siblings.forEach((siblingObj) => {
            const foundSiblingObj = allHtmlData.find((o) => o.id == siblingObj.id);
            if (foundSiblingObj) {
              updatedTopiContent.children.push(foundSiblingObj);
            }
          });
      }
      topicDataDetails.CONTENT.push(updatedTopiContent);
    });
    updatedTopicData.push(topicDataDetails);
  });
};

const getAllTopicsScoreKeywordWise = (topicId, keywordRelatedTopics = {}) => {
  const topicImportance = {};
  const topicImportanceRaw = {};
  Object.keys(keywordRelatedTopics).forEach((keywordName) => {
    const allTopics = [...keywordRelatedTopics[keywordName]];
    allTopics.forEach((topic) => {
      const isTopicFind = topic.TOPIC_ID === topicId;
      if (isTopicFind) {
        topicImportance[keywordName] = topic.TOPIC_IMPORTANCE || null;
        topicImportanceRaw[keywordName] = topic.TOPIC_IMPORTANCE_RAW || null;
      }
    });
    //// TODO change payload as per new TDM json here
    // const data = { ...kTopics.contentQuality };
    // const contentQualityRaw = { ...kTopics.contentQualityRaw };
    // data[forPageUrl] = kTopics.projected && kTopics.projected >= 0 ? kTopics.projected : kTopics.contentQuality[forPageUrl];
    // contentQualityAtKeywordLevel[keywordName] = { ...data }
    // contentQualityRawAtKeywordLevel[keywordName] = { ...contentQualityRaw }
  });
  return { topicImportance, topicImportanceRaw };
};

export const getContentQualityPayload = ({
  pageUrl,
  topic_id,
  keyword,
  id,
  tag,
  updatedText,
  lastTextBeforeEdit,
  childContent = [] as any[],
  pageLevelTopics,
  customerAllHtmlData = [] as any[],
  keywordRelatedTopics = {},
  tdmTopicsData,
  allKeywords = [] as any[],
  isNewSuggestedCopy = false,
  isFromKeywordsScreen = false,
}) => {
  let isAnyEditAvailable = false;
  const topicsRelatedAllHtmlData = pageLevelTopics || [];
  const topicsRelatedAllHtmlDataList = [...topicsRelatedAllHtmlData];
  if (isNewSuggestedCopy) {
    const indxObj = topicsRelatedAllHtmlDataList.findIndex((topics) => topics.TOPIC_ID == topic_id);
    if (indxObj >= 0) {
      isAnyEditAvailable = true;
      const suggestCopyData = {
        name: updatedText,
        IS_NEW: 1,
        url: pageUrl,
        flag: 1,
        html_tag: tag,
        html_id: id,
        children: [...childContent],
      };
      topicsRelatedAllHtmlDataList[indxObj].CONTENT.push(suggestCopyData);
    }
  }
  const allHtmlData = [...customerAllHtmlData] as any[];
  //    const  allTopicData = [...topicsRelatedAllHtmlDataList];
  const updatedTopicData = [] as any;
  topicsRelatedAllHtmlDataList.forEach((tData) => {
    const numSents = tData['NUM_SENTS_COMPETITORS'] + tData['NUM_SENTS_CUSTOMER'];
    const topicDataDetails = { ...tData, NUM_SENTS: numSents, CONTENT: [] };
    tData.CONTENT.forEach((tDataContent) => {
      if (tDataContent.flag) {
        // only for customer data
        const updatedTopiContent = { ...tDataContent, children: [] };
        let skipChild = false;
        // if (updatedTopiContent.html_id === id && topicDataDetails.TOPIC_ID === topic_id) {
        if (updatedTopiContent.html_id === id) {
          updatedTopiContent.name = updatedText;
          updatedTopiContent.old_name = lastTextBeforeEdit;
          updatedTopiContent.html_tag = tag;
          updatedTopiContent.IS_NEW = 1;
          skipChild = true; // bcz edited text found in parent, so no need to edit in siblings
          isAnyEditAvailable = true;
        }
        // && allData.id == parentElementId
        const foundObj = allHtmlData?.find((allData) => allData.id == tDataContent.html_id);
        const siblingsData = [...(foundObj?.siblings || []), ...(foundObj?.nestedSiblings || [])];
        const foundEditedChild = siblingsData.find(
          (siblingObj) => siblingObj.id == id && ONLY_HEADINGS_CHILD_TAGS.includes(siblingObj.tagName.toUpperCase())
        );
        // &&  topicDataDetails.TOPIC_ID === topic_id
        if (foundObj && !skipChild && foundEditedChild) {
          isAnyEditAvailable = true;
          const editedChild = {
            old_body: lastTextBeforeEdit,
            body: updatedText,
            url: pageUrl,
            flag: 1,
            html_tag: foundEditedChild.tagName,
            html_id: foundEditedChild.id,
            IS_NEW: 1,
          };
          updatedTopiContent.children.push(editedChild);
        } else if (isNewSuggestedCopy && tDataContent.children) {
          // // child data for newSuggested is not available in customerAllHtmlData, so for that added here
          updatedTopiContent.children.push(...tDataContent.children);
        }
        topicDataDetails.CONTENT.push(updatedTopiContent);
      }
    });
    const { topicImportance, topicImportanceRaw } = getAllTopicsScoreKeywordWise(tData.TOPIC_ID, keywordRelatedTopics);

    topicDataDetails.TOPIC_IMPORTANCE = {
      'page-level': tData.TOPIC_IMPORTANCE,
      ...topicImportance,
    };
    topicDataDetails.TOPIC_IMPORTANCE_RAW = {
      'page-level': tData.TOPIC_IMPORTANCE_RAW,
      ...topicImportanceRaw,
    };
    updatedTopicData.push(topicDataDetails);
  });
  const keywordsList = [] as any;
  allKeywords.forEach((k) => {
    if (k['Keyword Cluster'] !== 'Overall') {
      keywordsList.push({
        KEYWORD: k['Keyword Cluster'],
        KEYWORD_IMPORTANCE: k['Keyword Importance'],
      });
    }
  });
  const updatedContentQualityScore = tdmTopicsData?.contentQuality?.PROJECTED
    ? tdmTopicsData?.contentQuality?.PROJECTED
    : tdmTopicsData?.contentQuality?.CURRENT;

  const updatedContentQualityRawScore = tdmTopicsData?.contentQualityRaw;
  const payload = {
    URL: pageUrl,
    KEYWORD: keyword || null, // this will came when we allowed keyword level editing
    TOPICS: updatedTopicData || [],
    CONTENT_QUALITY: { ...updatedContentQualityScore },
    CONTENT_QUALITY_RAW: { ...updatedContentQualityRawScore },
    KEYWORD_INFO: keywordsList,
  };

  return !isFromKeywordsScreen ? payload : isFromKeywordsScreen && isAnyEditAvailable ? payload : null;
};

export const formatHtmlTagScore = (tagScore, readNewBodyTag = true) => {
  let returnObj = {};

  if (tagScore) {
    if (tagScore.description_hf_score != null) {
      returnObj['DESCRIPTION'] = tagScore.description_hf_score_projected
        ? tagScore.description_hf_score_projected
        : tagScore.description_hf_score;
    }
    if (readNewBodyTag) {
      if (tagScore.pre_hf_score != null) {
        returnObj['PRE'] = tagScore.pre_hf_score_projected ? tagScore.pre_hf_score_projected : tagScore.pre_hf_score;
      }

      if (tagScore.li_hf_score != null) {
        returnObj['LI'] = tagScore.li_hf_score_projected ? tagScore.li_hf_score_projected : tagScore.li_hf_score;
      }
      if (tagScore.span_hf_score != null) {
        returnObj['SPAN'] = tagScore.span_hf_score_projected
          ? tagScore.span_hf_score_projected
          : tagScore.span_hf_score;
      }
      if (tagScore.label_hf_score != null) {
        returnObj['LABEL'] = tagScore.label_hf_score_projected
          ? tagScore.label_hf_score_projected
          : tagScore.label_hf_score;
      }
    } else {
      if (tagScore.div_hf_score != null) {
        returnObj['DIV'] = tagScore.div_hf_score_projected ? tagScore.div_hf_score_projected : tagScore.div_hf_score;
      }
    }
    if (tagScore.h1_hf_score != null) {
      returnObj['H1'] = tagScore.h1_hf_score_projected ? tagScore.h1_hf_score_projected : tagScore.h1_hf_score;
    }
    if (tagScore.h2_hf_score != null) {
      returnObj['H2'] = tagScore.h2_hf_score_projected ? tagScore.h2_hf_score_projected : tagScore.h2_hf_score;
    }
    if (tagScore.h3_hf_score != null) {
      returnObj['H3'] = tagScore.h3_hf_score_projected ? tagScore.h3_hf_score_projected : tagScore.h3_hf_score;
    }
    if (tagScore.h4_hf_score != null) {
      returnObj['H4'] = tagScore.h4_hf_score_projected ? tagScore.h4_hf_score_projected : tagScore.h4_hf_score;
    }
    if (tagScore.h5_hf_score != null) {
      returnObj['H5'] = tagScore.h5_hf_score_projected ? tagScore.h5_hf_score_projected : tagScore.h5_hf_score;
    }
    if (tagScore.h6_hf_score != null) {
      returnObj['H6'] = tagScore.h6_hf_score_projected ? tagScore.h6_hf_score_projected : tagScore.h6_hf_score;
    }
    if (tagScore.images_hf_score != null) {
      returnObj['IMAGES'] = tagScore.images_hf_score_projected
        ? tagScore.images_hf_score_projected
        : tagScore.images_hf_score;
    }
    if (tagScore.jsonld_hf_score != null) {
      returnObj['JSONLD'] = tagScore.jsonld_hf_score_projected
        ? tagScore.jsonld_hf_score_projected
        : tagScore.jsonld_hf_score;
    }
    if (tagScore.links_hf_score != null) {
      returnObj['LINKS'] = tagScore.links_hf_score_projected
        ? tagScore.links_hf_score_projected
        : tagScore.links_hf_score;
    }
    if (tagScore.p_hf_score != null) {
      returnObj['P'] = tagScore.p_hf_score_projected ? tagScore.p_hf_score_projected : tagScore.p_hf_score;
    }
    if (tagScore.title_hf_score != null) {
      returnObj['TITLE'] = tagScore.title_hf_score_projected
        ? tagScore.title_hf_score_projected
        : tagScore.title_hf_score;
    }
  }

  return returnObj;
};

const getKSMV2Data = (keywordsData, customerAllHtmlData, fakeElement = []) => {
  let dataArr = [] as any[],
    keywordObj,
    parsedCustData,
    foundObj;

  keywordsData?.forEach((data) => {
    if (data.sub_keyword == null && data.keyword_emphasis !== null) {
      keywordObj = {};
      parsedCustData = data.customer_html_element_scores ? data.customer_html_element_scores.scores : null;
      keywordObj = {
        keyword: data.keyword,
        kis: data.keyword_importance,
        kes: data.keyword_emphasis_projected ? data.keyword_emphasis_projected : data.keyword_emphasis,
        is_branded: data.is_branded,
        element_scores: customerAllHtmlData?.map((ele) => {
          const foundObj = parsedCustData?.find((o) => Number(o?.element_id) == Number(ele?.id));
          return {
            id: ele?.id || foundObj?.element_id,
            score: foundObj?.projected_score ? foundObj?.projected_score : foundObj?.element_hf_score,
            tag: ele
              ? ele.tag == 'a'
                ? 'LINKS'
                : ele.tag == 'IMG'
                ? 'IMAGES'
                : ele.tag == 'Meta Description'
                ? 'DESCRIPTION'
                : ele.html_tag == 'JSONLD'
                ? 'JSONLD'
                : ele.tag.toUpperCase()
              : null,
          };
        }),
        // element_scores: parsedCustData
        //   ? parsedCustData.map((o) => {
        //       foundObj = customerAllHtmlData.find((k) => k.id == o.element_id);
        //       return {
        //         id: o.element_id,
        //         score: o.projected_score ? o.projected_score : o.element_hf_score,
        //         tag: foundObj
        //           ? foundObj.tag == 'a'
        //             ? 'LINKS'
        //             : foundObj.tag == 'IMG'
        //             ? 'IMAGES'
        //             : foundObj.tag == 'Meta Description'
        //             ? 'DESCRIPTION'
        //             : foundObj.html_tag == 'JSONLD'
        //             ? 'JSONLD'
        //             : foundObj.tag.toUpperCase()
        //           : null,
        //       };
        //     })
        //   : [],
      };
      if (!keywordObj?.element_scores?.length) {
        keywordObj.element_scores = fakeElement;
      }
      dataArr.push({ ...keywordObj });
    }
  });

  return dataArr;
};

const getHtmlElementsKSMV2 = (customerAllHtmlData) => {
  // Filter out objects with null, empty, or blank text
  const htmlElementsData = JSON.parse(JSON.stringify(customerAllHtmlData))
    ?.filter((o) => o.text && o.text.trim() !== '')
    .map((o) => {
      return {
        element_id: o.id,
        corpus: o.text,
        is_duplicate: o.is_duplicate,
      };
    });

  return htmlElementsData;
};

export const updateKeywordsWithNewScore = (
  ksmResponse,
  oldKeyWordData = [] as any[],
  tab,
  tag,
  isDeletion,
  suggestedCopyId
) => {
  let updatedKeywordsData = [] as any[];
  oldKeyWordData.forEach((keywordData) => {
    let isFound = false;
    ksmResponse.data.forEach((obj) => {
      if (keywordData.sub_keyword == null && obj.KEYWORD == keywordData.keyword) {
        let custHtml = keywordData.customer_html_element_scores;
        let custHTMLScore = [] as any[],
          isElementFound = false;
        custHtml.scores.forEach((custScore) => {
          if (custScore.element_id == ksmResponse.element_id) {
            isElementFound = true;
            custHTMLScore.push({ ...custScore, projected_score: isDeletion ? null : obj.element_score });
          } else {
            custHTMLScore.push(custScore);
          }
        });

        if (suggestedCopyId) {
          custHTMLScore.push({
            element_id: suggestedCopyId,
            element_hf_score: obj.element_score,
          });
        }

        if (!isElementFound) {
          custHTMLScore.push({
            element_id: ksmResponse.element_id,
            element_hf_score: 0,
            projected_score: obj.element_score,
          });
        }

        let custTagGroupScore = keywordData.customer_html_tag_group_scores;
        custTagGroupScore[`${tab.toLowerCase()}_projected`] = obj.tag_group_score;
        let custTagScore = keywordData.customer_html_tag_scores;
        if (tag == 'IMG') {
          custTagScore[`images_hf_score_projected`] = obj.tag_score;
        } else {
          custTagScore[`${tag.toLowerCase()}_hf_score_projected`] = obj.tag_score;
        }

        updatedKeywordsData.push({
          ...keywordData,
          keyword_emphasis_projected: obj.kes,
          customer_html_element_scores: { ...keywordData.customer_html_element_scores, scores: custHTMLScore },
          customer_html_tag_group_scores: { ...custTagGroupScore },
          customer_html_tag_scores: custTagScore,
        });
        isFound = true;
      }
    });
    if (!isFound) {
      updatedKeywordsData.push(keywordData);
    }
  });
  return updatedKeywordsData;
};

export const mapKeywordsClusterIds = (
  existingKwData: any[],
  newGeneratedKwData: any[],
  isForOnlyNonCompKeywords = false,
  pageUrl: any,
  isEditMode: boolean
) => {
  const nonCompKeywordClusters: { cluster_id: string; keyword: string }[] = [];
  const includedClustersOnly = existingKwData?.filter((kwd) => !kwd?.sub_keyword && kwd?.is_included) || [];
  if (!newGeneratedKwData?.length && !isForOnlyNonCompKeywords) {
    return [];
  }
  // if (!isEditMode) {
  //   newGeneratedKwData = newGeneratedKwData?.map((item) =>
  //     item.competitors.length <= 7 ? { ...item, competitors: [] } : item
  //   );
  // }
  const updatedClusterList: any = [];
  for (const cluster of includedClustersOnly) {
    const findCompData = newGeneratedKwData?.find(
      (ngk) => ngk?.keyword?.toLowerCase() === cluster?.keyword?.toLowerCase()
    );
    if (isForOnlyNonCompKeywords) {
      if (!findCompData?.competitors || findCompData?.competitors?.length < 5) {
        nonCompKeywordClusters.push({
          cluster_id: cluster?.cluster_id,
          keyword: cluster?.keyword,
        });
      }
    } else {
      let findCustomerMs;
      const clusterWiseCompList: any[] = [];
      for (const comp of findCompData?.competitors || []) {
        if (comp?.ms && isValidScore(comp?.ms)) {
          comp.ms = parseFloat(comp?.ms);
        } else {
          comp.ms = null;
        }
        if (comp?.name === pageUrl) {
          findCustomerMs = comp;
        } else {
          clusterWiseCompList.push(comp);
        }
      }
      updatedClusterList.push({
        cluster_id: cluster?.cluster_id,
        keyword: cluster?.keyword,
        competitors: clusterWiseCompList,
        keyword_importance: cluster?.keyword_importance,
        ms: findCustomerMs ? findCustomerMs?.ms : null,
      });
    }
  }
  if (isForOnlyNonCompKeywords) {
    return nonCompKeywordClusters;
  }
  return updatedClusterList;
};

interface FormatKeywordsTableDataArguments {
  data: any[];
  isContentQuality?: boolean;
  isPaidPage?: boolean;
  isKeywordsForDropdown?: boolean;
  asFilter?: boolean;
  isFromContentBriefScreen?: boolean;
  topicsData?: any[];
  isGetContentQuality?: boolean;
  _keyword4CompetitorsList?: () => any;
}

export const formatKeywordsTableData = ({
  data,
  isContentQuality = false,
  isPaidPage = false,
  isKeywordsForDropdown = false,
  asFilter = false,
  isFromContentBriefScreen = false,
  topicsData,
  isGetContentQuality = false,
  _keyword4CompetitorsList = () => {},
}: FormatKeywordsTableDataArguments) => {
  const { mainHeading = [], subHeading = {} as any } = !isContentQuality ? INTENT_GRID_DETAILS : {}; //add here for content instead of {}

  const gridDetails = { ...subHeading };

  let parentRecords = [] as any[],
    formattedData = [] as any[],
    subRecords = [] as any[];
  const keywordsDropdownList = [] as any[];

  parentRecords = data.filter((obj) => !obj['sub_keyword']);
  const paidClicks = parentRecords.reduce((total, obj) => obj?.paid_clicks + total, 0);
  const orgClicks = parentRecords.reduce((total, obj) => obj?.monthly_clicks + total, 0);
  gridDetails.keywordType.isHidden = paidClicks <= 0 && orgClicks <= 0 ? true : false;
  parentRecords?.forEach((parentObj, index) => {
    const { currentQualityScore, projectedQualityScore, qualityRank } = getCurrentAndProjectedQuality({
      parentObj,
      topicsData,
      isGetContentQuality,
    });

    subRecords = [] as any[];
    data.forEach((record) => {
      if (record['sub_keyword'] && record['keyword'] == parentObj['keyword']) {
        const intentEmpValue = record.keyword_emphasis_projected
          ? record.keyword_emphasis_projected
          : record.keyword_emphasis;
        subRecords.push({
          [gridDetails.keywordCluster.name]: record.sub_keyword,
          [gridDetails?.keywordType?.name]: (
            <CRKeywordOpportunity
              pageSource={isPaidPage ? PAGE_SOURCE.PAID : PAGE_SOURCE.ORG}
              monthly_clicks={record.monthly_clicks}
              paid_clicks={record.paid_clicks}
              monthly_impressions={record.monthly_impressions}
              paid_impressions={record.paid_impressions}
            />
          ),
          [gridDetails.keywordImportance.name]: isValidScore(record?.sub_keyword_importance)
            ? record?.sub_keyword_importance
            : record.keyword_importance,
          [gridDetails.IntentEmphasis.name]: intentEmpValue,
          [gridDetails.emphasisRank.name]: null,
          [gridDetails.avgOrgPosition.name]: record.average_position,
          // Clicks and impressions are monthly averages over a four-month window and
          // genuinely fractional. Ceiling them here made 0.25 a month read as 1 before
          // the cell ever saw the value; the cell's formatMeasuredCount rounds the way
          // the page header does, so hand it the measurement.
          [gridDetails.orgImpressions.name]: checkValueExists(record.monthly_impressions)
            ? numberFormatter(record.monthly_impressions)
            : null,
          // [gridDetails.ctr.name]: isPaidPage ? record.paid_ctr : record.ctr,
          [gridDetails.ctr.name]: record.ctr, //// as suggested by Anurag, CTR is always for ORG only
          [gridDetails.paidImpressions.name]: checkValueExists(record.paid_impressions)
            ? numberFormatter(record.paid_impressions)
            : null,
          [gridDetails.cpc.name]: record.cpc,
          [gridDetails.cpa.name]: record.cpa,
          [gridDetails.clusterCount.name]: ` `,
          [gridDetails.orgClicks.name]: checkValueExists(record.monthly_clicks)
            ? numberFormatter(record.monthly_clicks)
            : null,
          [gridDetails.paidClicks.name]: checkValueExists(record.paid_clicks)
            ? numberFormatter(record.paid_clicks)
            : null,

          [gridDetails.intentEmphasisDelta.name]: record.keyword_emphasis_projected
            ? record.keyword_emphasis_projected - record.keyword_emphasis
            : null,
          // A CPC is money: $0.37 ceiled to $1 is not the same number. Hand the
          // cell the value and let it format the cents.
          [gridDetails.avgCPC.name]: checkValueExists(record.average_cpc)
            ? Number(Number(record.average_cpc).toFixed(2))
            : null,
          [gridDetails.avgSearchValue.name]: checkValueExists(record.search_volume)
            ? numberFormatter(Math.ceil(record.search_volume))
            : null,
          [gridDetails.keywordDifficulty.name]: record.keyword_difficulty,
          clicks: Math.ceil(record.monthly_clicks),
          is_branded: record.is_branded,
          keyword_source: record?.keyword_source,
          cluster_source: record?.cluster_source,
          is_cpk_data: record?.is_cpk_data,
        });
      }
    });

    const { currentEmphasisRank, projectedEmphasisRank } = getCurrentAndProjectedRank({
      parentObj,
      _keyword4CompetitorsList,
    });

    //newEmphasisRank =index; // for testing remove this line latter
    const intentEmphasisValue = parentObj.keyword_emphasis_projected
      ? parentObj.keyword_emphasis_projected
      : parentObj.keyword_emphasis;
    const parentKeywordsData = {
      [gridDetails.keywordCluster.name]: parentObj.keyword,
      [gridDetails.NoOfKeywords.name]: parentObj.cluster_count,
      [gridDetails?.keywordType?.name]: (
        <CRKeywordOpportunity
          pageSource={isPaidPage ? PAGE_SOURCE.PAID : PAGE_SOURCE.ORG}
          monthly_clicks={parentObj.monthly_clicks}
          paid_clicks={parentObj.paid_clicks}
          monthly_impressions={parentObj.monthly_impressions}
          paid_impressions={parentObj.paid_impressions}
        />
      ),
      [gridDetails.keywordImportance.name]: parentObj.keyword_importance,
      [gridDetails.IntentEmphasis.name]: intentEmphasisValue,
      [`${gridDetails.IntentEmphasis.name}_current`]: parentObj?.keyword_emphasis || null,
      [`${gridDetails.IntentEmphasis.name}_projected`]: parentObj?.keyword_emphasis_projected || null,
      [gridDetails.emphasisRank.name]: projectedEmphasisRank ? projectedEmphasisRank : currentEmphasisRank,
      [gridDetails.emphasisRankOld.name]:
        !isFromContentBriefScreen && projectedEmphasisRank ? currentEmphasisRank : null,
      [gridDetails.avgOrgPosition.name]: parentObj.average_position,
      [gridDetails.orgImpressions.name]: checkValueExists(parentObj.monthly_impressions)
        ? numberFormatter(parentObj.monthly_impressions)
        : null,
      // [gridDetails.ctr.name]: isPaidPage ? parentObj.paid_ctr : parentObj.ctr,
      [gridDetails.ctr.name]: parentObj.ctr, //// as suggested by Anurag, CTR is always for ORG only
      [gridDetails.paidImpressions.name]: checkValueExists(parentObj.paid_impressions)
        ? numberFormatter(parentObj.paid_impressions)
        : null,
      [gridDetails.cpc.name]: parentObj.cpc,
      [gridDetails.cpa.name]: parentObj.cpa,
      [gridDetails.clusterCount.name]: asFilter ? `${parentObj.cluster_count} keywords` : null,
      [gridDetails.orgClicks.name]: checkValueExists(parentObj.monthly_clicks)
        ? numberFormatter(parentObj.monthly_clicks)
        : null,
      [gridDetails.paidClicks.name]: checkValueExists(parentObj.paid_clicks)
        ? numberFormatter(Math.ceil(parentObj.paid_clicks))
        : null,
      [gridDetails.intentEmphasisDelta.name]: parentObj.keyword_emphasis_projected
        ? parentObj.keyword_emphasis_projected - parentObj.keyword_emphasis
        : null,
      [gridDetails.avgCPC.name]: checkValueExists(parentObj.average_cpc)
        ? Number(Number(parentObj.average_cpc).toFixed(2))
        : null,
      [gridDetails.avgSearchValue.name]: checkValueExists(parentObj.search_volume)
        ? numberFormatter(Math.ceil(parentObj.search_volume))
        : null,
      [gridDetails.keywordDifficulty.name]: parentObj.keyword_difficulty,

      clicks: Math.ceil(parentObj.monthly_clicks),
      is_branded: parentObj.is_branded,
      isHidden: parentObj.isHidden,
      is_included: parentObj?.is_included,
      is_cluster_included: parentObj?.is_cluster_included,
      Keywords: parentObj?.cluster_count || subRecords?.length,
      cluster_id: parentObj?.cluster_id,
      keyword_source: parentObj?.keyword_source,
      cluster_source: parentObj?.cluster_source,
      is_cpk_data: parentObj?.is_cpk_data,
      current_keyword_quality_score: isGetContentQuality ? currentQualityScore || null : null,
      projected_keyword_quality_score: isGetContentQuality ? projectedQualityScore || null : null,
      keyword_quality_rank: isGetContentQuality ? qualityRank || null : null,
      competitor_html_tag_group_scores: parentObj?.competitor_html_tag_group_scores || null,
    };
    if (isKeywordsForDropdown && (isValidScore(intentEmphasisValue) || isFromContentBriefScreen)) {
      keywordsDropdownList.push({ ...parentKeywordsData });
    }
    formattedData.push({
      ...parentKeywordsData,
      subRows:
        subRecords.length <= 0
          ? []
          : [...(sortArrayByKey(subRecords, 'desc', gridDetails.keywordImportance.name) || [])],
    });
  });
  return isKeywordsForDropdown ? keywordsDropdownList : formattedData || [];
};

export const getCurrentAndProjectedRank = ({ parentObj, _keyword4CompetitorsList = () => [] }) => {
  const keyword4CompetitorsList = _keyword4CompetitorsList ? _keyword4CompetitorsList() : [];

  const top4KeywordComp = keyword4CompetitorsList?.length
    ? allKeyword4CompetitorsListData(keyword4CompetitorsList, parentObj)?.competitors
    : [];
  const isFindRank = ![undefined, null, ''].includes(parentObj?.competitor_keyword_emphasis_scores);
  if (!isFindRank) {
    return { currentEmphasisRank: null, projectedEmphasisRank: null };
  }
  const parsedCompetitorData =
    parentObj?.competitor_keyword_emphasis_scores && parentObj?.competitor_keyword_emphasis_scores !== 'null'
      ? safelyParse(parentObj?.competitor_keyword_emphasis_scores, true)
      : [];

  const filteredParsedCompetitorData = top4KeywordComp?.length
    ? (isArray(parsedCompetitorData) ? parsedCompetitorData : [])?.filter((pcomp) =>
        top4KeywordComp?.some((tcomp) => tcomp?.name === pcomp?.competitor_page)
      )
    : isArray(parsedCompetitorData)
    ? parsedCompetitorData
    : [];
  const competitorsKESScores =
    parsedCompetitorData && isArray(filteredParsedCompetitorData)
      ? [...(filteredParsedCompetitorData?.map((ele) => ele.kes) || [])]
      : [];
  const validCurrentScore = isValidScore(parentObj?.keyword_emphasis) ? parentObj?.keyword_emphasis : null;
  const currentScoresList = [validCurrentScore, ...competitorsKESScores].sort((a, b) => b - a);
  const currentEmphasisRankIndex = currentScoresList.findIndex((o) => o === validCurrentScore);
  const currentEmphasisRank = validCurrentScore
    ? currentEmphasisRankIndex + 1 >= 5
      ? '>4'
      : currentEmphasisRankIndex + 1
    : null;

  const validProjectedScore = isValidScore(parentObj?.keyword_emphasis_projected)
    ? parentObj?.keyword_emphasis_projected
    : null;
  const projectedScoresList = [validProjectedScore, ...competitorsKESScores].sort((a, b) => b - a);
  const projectedEmphasisRankIndex = projectedScoresList.findIndex((o) => o === validProjectedScore);
  let projectedEmphasisRank: number | string | null = validProjectedScore
    ? Math.floor(projectedEmphasisRankIndex) + 1 >= 5
      ? '>4'
      : Math.floor(projectedEmphasisRankIndex) + 1
    : null;

  if (currentEmphasisRank === projectedEmphasisRank) {
    projectedEmphasisRank = null;
  }
  return { currentEmphasisRank, projectedEmphasisRank };
};

const allKeyword4CompetitorsListData = (keyword4CompetitorsList, parentObj) => {
  const findKeywordCompObj = keyword4CompetitorsList?.find(
    (allComp) => allComp?.keyword?.toLowerCase() === parentObj?.keyword?.toLowerCase()
  ) || { competitors: [] };
  const keywordCompList = findKeywordCompObj?.competitors?.filter((comp) =>
    [true, 'true', 'TRUE'].includes(comp?.is_selected)
  );
  return { competitors: keywordCompList };
};

const getCurrentAndProjectedQuality = ({ parentObj, topicsData, isGetContentQuality }) => {
  const keyword = parentObj?.keyword;
  const url = topicsData?.URL;

  const getRankdetails = (status: string) => {
    const contentQualityData = topicsData?.contentQuality[status]?.[keyword] || {};

    const sortedDataArray = Object.entries(contentQualityData as Record<string, number>).sort(
      ([, scoreA], [, scoreB]) => scoreB - scoreA
    );

    const sortedData = Object.fromEntries(sortedDataArray);

    const sortedUrls = Object.keys(sortedData);
    const position = sortedUrls.indexOf(url) + 1;
    return position > 0 ? position : null;
  };

  const current = Math.ceil(topicsData?.contentQuality?.CURRENT?.[keyword]?.[url]) || null;
  const projected = Math.ceil(topicsData?.contentQuality?.PROJECTED?.[keyword]?.[url]) || null;
  const rank = projected ? getRankdetails('PROJECTED') : current ? getRankdetails('CURRENT') : null;
  let rankBasedOnScore = 0;
  if (isValidScore(projected) && projected === 100) {
    rankBasedOnScore = 1;
  } else if (!isValidScore(projected) && isValidScore(current) && current === 100) {
    rankBasedOnScore = 1;
  }
  return {
    currentQualityScore: current,
    projectedQualityScore: projected,
    qualityRank: rankBasedOnScore ? rankBasedOnScore : rank,
  };
};

export const getPositiveOrNegative = (value) => {
  if (checkValueExists(value)) {
    if (value >= 0) {
      return { className: 'positive', value: `+${value}` };
    }
    return { className: 'negative', value: value };
  }
  return null;
};

export const compareWithEachCustomerTabs = (
  tabName,
  customerTagGroupScoreData,
  competitorTabsData = [],
  keywordWiseCompWithScores = []
) => {
  const compTabName = tabName?.toLowerCase() === 'structured data' ? 'structured_data' : tabName;
  const customerScoreForTab = isValidScore(customerTagGroupScoreData?.[`${tabName}_projected`])
    ? customerTagGroupScoreData?.[`${tabName}_projected`]
    : customerTagGroupScoreData?.[tabName];
  const allCompetitorUrls = keywordWiseCompWithScores.map((item: any) => item?.name);
  const allCompTabNamesUndefined = competitorTabsData
    ?.filter((customerTab: any) => allCompetitorUrls?.includes(customerTab?.competitor_page))
    .every((customerTab) => customerTab?.[compTabName] === undefined);
  const competitorTabScoreData =
    competitorTabsData
      ?.filter((customerTab: any) => allCompetitorUrls?.includes(customerTab?.competitor_page))
      .map((customerTab) => {
        const score = customerTab?.[compTabName];
        // Check if score is either 0 or undefined
        if (score === undefined || score === 0) {
          return null;
        }
        return score > customerScoreForTab && !allCompTabNamesUndefined;
      })
      .filter((score) => score !== null) || [];
  return !competitorTabScoreData.includes(true);
};

export const generateParams = (paramsObj = [] as any[], row = {}, preConcatString) => {
  if (row && Object.keys(row).length > 0) {
    const hasCustomerData = Object.keys(row).some(
      (key) => key.endsWith('_is_customer') && !row[key] && row[`${key.replace('_is_customer', '_current')}`]
    );
    if (!hasCustomerData) {
      Object.keys(row).forEach((key) => {
        if (key.endsWith('_is_customer') && !row[key]) {
          const baseKey = key.replace('_is_customer', '');
          if (row[`${baseKey}_current`] === null || row[`${baseKey}_current`] === undefined) {
            row['winningScore'] = 100;
          }
        }
      });
    }
  }

  const customerScores = Object.keys(row)
    .filter((key) => key.endsWith('_is_customer') && row[key])
    .map((key) => {
      const baseKey = key.replace('_is_customer', '');
      return row[`${baseKey}_projected`] ?? row[`${baseKey}_current`];
    });

  const competitorScores = Object.keys(row)
    .filter((key) => key.endsWith('_is_customer') && !row[key])
    .map((key) => row[`${key.replace('_is_customer', '_current')}`]);

  const hasMatchingScores = customerScores.some(
    (custScore) =>
      competitorScores.includes(custScore) &&
      custScore !== null &&
      customerScores.some((custScore) => custScore >= Math.max(...competitorScores))
  );

  const newParams = {};
  paramsObj.forEach((params) => {
    newParams[params.passKey] = row[`${preConcatString}${params.dataKey}`];
    if (params.isFlag && params.passKey === 'isWinner') {
      const isCustomer = row[`${preConcatString}${params.dataKey}_is_customer`];
      if (hasMatchingScores) {
        newParams[params.passKey] = isCustomer;
      } else {
        newParams[params.passKey] = row[`${preConcatString}${params.dataKey}`] === row['winningScore'];
      }
    } else if (params.isFlag) {
      newParams[params.passKey] = eval(
        `${row[`${preConcatString}${params.dataKey}`]} && ${row[`${preConcatString}${params.dataKey}`]} ${
          params.condition
        } ${row[params.conditionColName]}`
      );
    }
  });
  return newParams;
};

export const getOverallHtmlElementScores = (tabData, overall_element_scores: any[]) => {
  let updatedTabData = tabData;
  let suggestedData = [] as any[],
    siblingsData,
    updatedObj,
    siblingHtmlScore;
  let elementScores = overall_element_scores;
  if (elementScores && elementScores.length) {
    updatedTabData = [] as any[];
    suggestedData = [] as any[];
    tabData.forEach((data) => {
      let htmlScore: any | null = null;
      siblingsData = [] as any[];
      htmlScore = elementScores.find((custData) => custData.element_id == data.id);
      if (data.isSuggested) {
        let obj = { ...data };
        if (htmlScore) {
          obj = { ...obj, htmlElementScore: htmlScore.score, projectedHtmlElementScore: htmlScore.projected_score };
        }
        // assign scores to siblings data
        if (data.siblings && data.siblings.length) {
          data.siblings.forEach((siblingObj) => {
            siblingHtmlScore = elementScores.find((custData) => custData.element_id == siblingObj.id);
            if (siblingHtmlScore) {
              siblingsData.push({
                ...siblingObj,
                htmlElementScore: siblingHtmlScore.score,
                projectedHtmlElementScore: siblingHtmlScore.projected_score,
              });
            } else {
              siblingsData.push({ ...siblingObj, htmlElementScore: -1 });
            }
          });

          obj = { ...obj, siblings: siblingsData };
        }

        suggestedData.push({ ...obj });
      } else {
        if (htmlScore) {
          updatedObj = {
            ...data,
            htmlElementScore: htmlScore.score,
            projectedHtmlElementScore: htmlScore.projected_score,
          };
        } else {
          updatedObj = { ...data, htmlElementScore: -1 };
        }
        // assign scores to siblings data
        if (data.siblings && data.siblings.length) {
          data.siblings.forEach((siblingObj) => {
            const tagName = siblingObj.tag;
            if (ONLY_HEADINGS_CHILD_TAGS.includes(tagName.toUpperCase())) {
              siblingHtmlScore = elementScores.find((custData) => custData.element_id == siblingObj.id);
              if (siblingHtmlScore) {
                siblingsData.push({
                  ...siblingObj,
                  htmlElementScore: siblingHtmlScore.score,
                  projectedHtmlElementScore: siblingHtmlScore.projected_score,
                });
              } else {
                siblingsData.push({ ...siblingObj, htmlElementScore: -1 });
              }
            }
          });

          updatedObj = { ...updatedObj, siblings: siblingsData };
        }

        updatedTabData.push(updatedObj);
      }
    });

    return [...suggestedData, ...updatedTabData.sort((a, b) => b.htmlElementScore - a.htmlElementScore)];
  }

  return tabData;
};

export const rankChecked = (v) => (v > 4 ? `>${4}` : v);

export const formatTopicsTableData = ({
  gridDetails,
  contentQuality = {},
  keywordsData,
  topicsDataArr,
  selectedCompetitorsForTopic,
  topicsAllHtmlData,
  topicsCompetitorHtmlData,
  customerUrl,
  countOfCompetitorPrevalencePageLevel,
  isFromContentBriefScreen = false,
  _keyword4CompetitorsList = () => {},
}) => {
  let keywordDataObj, projectedEmphasisRank, currentEmphasisRank;
  // eslint-disable-next-line
  const formattedData = [] as any[],
    parentRecords = keywordsData.filter((obj) => !obj['sub_keyword']);
  // const competitorLength = Object.keys(contentQuality).length > 0 ? Object.keys(contentQuality).length - 1 : 0;
  //// NOTE: shoowing Competitor Prevalence, Topic headings count and same as page level even after keyword changes, so passing same page level values here
  const competitorLength = countOfCompetitorPrevalencePageLevel;

  topicsDataArr.forEach((topicData) => {
    const updatedKeywordsInfo = [] as any[];
    topicData.KEYWORD_INFO &&
      topicData.KEYWORD_INFO.forEach((topKeyword) => {
        keywordDataObj = {};
        keywordDataObj = parentRecords.filter((key) => key.keyword == topKeyword.KEYWORD);
        keywordDataObj = keywordDataObj && keywordDataObj.length ? keywordDataObj[0] : null;
        if (keywordDataObj && !keywordDataObj.isHidden) {
          const { currentEmphasisRank, projectedEmphasisRank } = getCurrentAndProjectedRank({
            parentObj: keywordDataObj,
            _keyword4CompetitorsList: _keyword4CompetitorsList as any,
          });
          if (keywordDataObj) {
            updatedKeywordsInfo.push({
              ...topKeyword,
              keyword_emphasis_projected: keywordDataObj.keyword_emphasis_projected,
              keyword_emphasis: keywordDataObj.keyword_emphasis,
              current_emphasis_rank: isFromContentBriefScreen ? null : currentEmphasisRank,
              projected_emphasis_rank:
                projectedEmphasisRank && currentEmphasisRank !== projectedEmphasisRank ? projectedEmphasisRank : null,
              average_position: keywordDataObj.average_position,
              monthly_clicks: keywordDataObj.monthly_clicks,
              ctr: keywordDataObj.ctr,
            });
          } else {
            updatedKeywordsInfo.push({
              ...topKeyword,
              keyword_emphasis_projected: null,
              keyword_emphasis: null,
              current_emphasis_rank: currentEmphasisRank,
            });
          }
        }
      });
    const { improveHeadingData, customerHtmlContent, competitorHtmlContent, topicLevelUpdatedInfo } = getContentHtml(
      topicData.CONTENT,
      topicsAllHtmlData,
      topicsCompetitorHtmlData,
      selectedCompetitorsForTopic
    );

    // find out the min & max no of sentenses for urls
    const noOfSentensesCountPerUrlObj = {};
    improveHeadingData.forEach((o) => {
      if (!o.isCustomer) {
        if (noOfSentensesCountPerUrlObj[o.url]) {
          noOfSentensesCountPerUrlObj[o.url] += o.num_sents;
        } else {
          noOfSentensesCountPerUrlObj[o.url] = o.num_sents;
        }
      }
    });

    const sortedUrlKeys = Object.keys(noOfSentensesCountPerUrlObj).sort(
      (a, b) => noOfSentensesCountPerUrlObj[a] - noOfSentensesCountPerUrlObj[b]
    );
    const noOfSentensesRange =
      sortedUrlKeys.length > 1
        ? `${noOfSentensesCountPerUrlObj[sortedUrlKeys[0]]} - ${
            noOfSentensesCountPerUrlObj[sortedUrlKeys[sortedUrlKeys.length - 1]]
          }`
        : noOfSentensesCountPerUrlObj[sortedUrlKeys[0]];
    if (
      selectedCompetitorsForTopic.length === 0 ||
      checkUrlIsExist([...selectedCompetitorsForTopic, customerUrl], [...new Set(improveHeadingData.map((m) => m.url))])
    ) {
      formattedData.push({
        [gridDetails.topicImportance.name]: Math.ceil(topicData.TOPIC_IMPORTANCE),
        [gridDetails.compPrevalence.name]:
          competitorLength > 0 ? `${topicData.COMPETITOR_PREVALENCE} of ${competitorLength}` : null,
        [gridDetails.topic.name]: topicData.TOPIC,
        [gridDetails.topicId.name]: topicData.TOPIC_ID,
        [gridDetails.customer.name]: topicData.NUM_SENTS_CUSTOMER,
        [CONTENT_QUALITY_TBL.CUSTOMER]: topicData.NUM_SENTS_CUSTOMER,
        [gridDetails.competitors.name]: noOfSentensesRange,
        [gridDetails.headingsCount.name]: `${improveHeadingData.length} Headings`,
        [gridDetails.logoUrls.name]: [...new Set(improveHeadingData.map((m) => m.url))], // removing duplicate urls
        expandedViewData: { improveHeadingData, keywords: updatedKeywordsInfo },
        htmlContent: { customerHtmlContent, competitorHtmlContent },
        updatedInfo: { ...topicLevelUpdatedInfo },
        // isRowExpand: checkUrlIsExist(selectedCompetitorsForTopic, [... new Set(improveHeadingData.map(m => m.url))] )
      });
    }
  });
  return formattedData;
};

export const checkUrlIsExist = (mainUrls = [] as any[], urls = [] as any[]) => {
  const extractName = mainUrls.map((mainUrl) => extractCompetitorName(mainUrl));
  const isFound = urls.some((url) => extractName.includes(extractCompetitorName(url)));
  return isFound;
};
export const findPos = (obj, defaultPos = -150) => {
  let curtop = defaultPos;
  if (obj?.offsetParent) {
    do {
      curtop += obj?.offsetTop;
    } while ((obj = obj?.offsetParent));
    return [curtop];
  }
};

export const mappedPageLevelAndKeywordTopics = (tdmTopicsData = [] as any[]) => {
  const pageLevelTopicsData = [] as any[];
  const keywordsLevelTopicsData = {};
  tdmTopicsData.forEach((tdmTopic) => {
    const TOPIC = tdmTopic.TOPIC;
    const TOPIC_ID = tdmTopic.TOPIC_ID;
    if (tdmTopic.TOPICS['page-level']) {
      pageLevelTopicsData.push({
        TOPIC,
        TOPIC_ID,
        ...tdmTopic.TOPICS['page-level'],
      });
    }
    const keysList = Object.keys(tdmTopic.TOPICS).filter((k) => k != 'page-level' && tdmTopic.TOPICS[k] != null);
    keysList.forEach((k) => {
      if (!keywordsLevelTopicsData[k]) {
        keywordsLevelTopicsData[k] = [
          {
            TOPIC,
            TOPIC_ID,
            ...tdmTopic.TOPICS[k],
          },
        ];
      } else {
        keywordsLevelTopicsData[k].push({
          TOPIC,
          TOPIC_ID,
          ...tdmTopic.TOPICS[k],
        });
      }
    });
  });
  return {
    pageLevelTopicsData,
    keywordsLevelTopicsData,
  };
};
export function metaDataList(tabsdata) {
  const metaData = [
    { text: 'H1', value: 'H1' },
    { text: 'TITLE', value: 'Title' },
    { text: 'META DESCRIPTION', value: 'Meta Description' },
  ];
  const newTabData = [] as any[];
  metaData.forEach((element) => {
    const foundElement = tabsdata.find((tab) => tab.tag.toUpperCase() == element.text.toUpperCase());
    if (!foundElement) {
      newTabData.push(element);
    }
  });
  return newTabData;
}

export async function fetchCrTopicsData(url, content_recommendation_id) {
  console.log('cr-common :: fetchCrTopicsData:: url, content_recommendation_id', url, content_recommendation_id);
  try {
    const apiRequest = `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/recommendations/content-recommendations/get-topics-v2`;
    const response = await ApiService.post(apiRequest, { url: url, content_recommendation_id });
    if (response?.data) {
      return response?.data;
    }
    return {};
  } catch (error) {
    console.error('common : fetchCrTopicsData err', error);
    throw error;
  }
}

export async function fetchGptText(
  text: string,
  tag: string | undefined | null,
  tokenLimit = 2000,
  isPublicApi?: boolean
) {
  try {
    const api = !isPublicApi
      ? `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/utility/get-auto-gpt-text`
      : `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/external-api/get-auto-gpt-text`;
    const responseAutoText = await (isPublicApi ? QwaPublicAPIAccess : ApiService).post(api, {
      prompt: text?.replace(/(<([^>]+)>)/gi, ''),
      max_tokens: tokenLimit,
    });
    return (
      responseAutoText?.data?.choices?.[0]?.message?.content ||
      responseAutoText?.data?.choices?.[0]?.text ||
      responseAutoText.data
    )?.replace(/\n+$/, '');
  } catch (error) {
    console.error('cr-common :: fetchGptText:: err::', error);
    throw error;
  }
}
interface FetchAIBasedContentParams {
  promptFor: string;
  promptConfig: { [key: string]: any }; // Adjust this type according to the shape of your promptConfig
  reCAPTCHAToken?: string | null; // Making it optional
  tokenLimit?: number; // Also making tokenLimit optional with a default value
  isPublicApi?: boolean;
  callingFor?: string;
  gptModel?: string | undefined;
  getKsmScores?: boolean;
  ksmPayload?: any;
}
//// New method for promptConfig, try to avoid use of fetchGptText() from cr-common.tsx
export function fetchAIBasedContent({
  promptFor,
  promptConfig,
  reCAPTCHAToken,
  tokenLimit = 2000,
  isPublicApi,
  callingFor = '',
  gptModel = '',
  getKsmScores = false,
  ksmPayload,
}: FetchAIBasedContentParams) {
  return new Promise<any>(async (resolve, reject) => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const api = !isPublicApi
        ? `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/utility/get-auto-gpt-text`
        : `${process.env.NEXT_PUBLIC_HOST_URL}/api/quattr-content-ai/autowrite`;
      const apiBody = {
        promptFor,
        promptConfig,
        max_tokens: tokenLimit,
        timezone,
        callingFor,
        isDeveloperTest: true,
        gptModel,
        getKsmScores,
        ksmPayload,
      };

      const processApiResponse = (response) => {
        const data =
          response?.data?.data ||
          response?.data?.choices?.[0]?.message?.content ||
          response?.data?.choices?.[0]?.text ||
          response?.data ||
          response;
        return typeof data != 'string' ? data : data?.replace(/\n+$/, '') || '';
      };

      if (isPublicApi && window.grecaptcha) {
        if (reCAPTCHAToken) {
          apiBody['reCAPTCHAToken'] = reCAPTCHAToken;
        }
        try {
          const responseAutoText = await (isPublicApi ? QwaPublicAPIAccess : ApiService).post(api, apiBody);
          resolve(processApiResponse(responseAutoText));
        } catch (error) {
          reject(error);
        }
      } else {
        const responseAutoText = await (isPublicApi ? QwaPublicAPIAccess : ApiService).post(api, apiBody);
        resolve(processApiResponse(responseAutoText));
      }
      setCookie('ToolUsed', true, true);
    } catch (error: any) {
      // Public tool errors can contain the submitted prompt in Axios config.
      if (!isPublicApi) console.error('cr-common :: fetchAIBasedContent:: err::', error);
      if (
        [403, 401].includes(error?.response?.data?.status_code) &&
        ['Requires Authentication', 'Access Denied!!!'].includes(error?.response?.data?.message)
      ) {
        signOut({ callbackUrl: '/login', redirect: true });
        trackAnalyticsEvent({
          eventName: 'Logged out due to session expiry',
          payload: {
            label: 'Logged out due to session expiry',
            methodCalled: 'fetchAIBasedContent',
          },
        });
      }
      reject(error);
    }
  });
}

export function streamRewriteText(payload: any) {
  return new Promise<any>(async (resolve, reject) => {
    try {
      const gptModel = typeof payload?.gptModel === 'string' ? payload.gptModel.trim() : '';
      if (!gptModel) {
        reject(new Error('streamRewriteText requires gptModel'));
        return;
      }
      const api = `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/utility/stream-rewrite-text`;
      const response = await ApiService.post(api, streamRewriteTextBody({ ...payload, gptModel }));
      resolve(response?.data?.data || response?.data);
    } catch (error) {
      reject(error);
    }
  });
}

export function streamGenerateFaqs(payload: any) {
  return new Promise<any>(async (resolve, reject) => {
    try {
      // TODO: Replace with dedicated FAQ endpoint when backend is ready
      const api = `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/utility/stream-generate-faqs`;
      const response = await ApiService.post(api, payload);
      resolve(response?.data?.data || response?.data);
    } catch (error) {
      reject(error);
    }
  });
}

export async function fetchKeywordDfsData(payload, isPublicApi = false) {
  try {
    const api = isPublicApi
      ? `${process.env.NEXT_PUBLIC_HOST_URL}/api/quattr-content-ai/autowrite`
      : `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/recommendations/content-recommendations/fetch-dfs-keyword`;
    const response = await (isPublicApi ? QwaPublicAPIAccess : ApiService).post(
      api,
      isPublicApi ? { ...payload, promptFor: 'PUBLIC_UTILITY.GET_KEYWORD_STATS' } : payload
    );
    return response;
  } catch (error) {
    if (!isPublicApi) console.error('cr-common :: fetchKeywordDfsData:: err::', error);
    throw error;
  }
}

export const getParamName = (tag) => {
  let updatedTag = tag.toLowerCase();
  if (updatedTag == 'title') {
    return 'title';
  }
  if (updatedTag == 'meta description') {
    return 'description';
  }
  if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(updatedTag)) {
    return updatedTag;
  }
  if (updatedTag == 'img') {
    return 'images';
  }
  if (updatedTag == 'a') {
    return 'links';
  }
  if (updatedTag == 'li') {
    return 'li';
  }
  if (updatedTag == 'span') {
    return 'span';
  }
  if (updatedTag == 'div') {
    return 'div';
  }
  if (updatedTag == 'pre') {
    return 'pre';
  }
  if (updatedTag == 'p') {
    return 'p';
  }
  if (updatedTag == 'label') {
    return 'label';
  }
  return 'jsonld';
};

export const getKewordrelatedAndSynonumsData = (keywordsData) => {
  const Alldata = [] as any[];
  keywordsData?.map((item) => {
    const comp_extracted_keywordsList = JSON.parse(item.comp_extracted_keywords)?.map((extracted_keywords) => {
      return {
        term: extracted_keywords.KEYWORD,
        score: extracted_keywords.KEYWORD_IMPORTANCE,
        occurances: 0,
      };
    });
    const kwdRelatedTerm = safelyParse(item.keyword_related_terms, true) || [];
    const keyword_related_termsList = !Array.isArray(kwdRelatedTerm)
      ? []
      : kwdRelatedTerm?.map((related_terms) => {
          return {
            term: related_terms.KEYWORD,
            score: related_terms.KEYWORD_IMPORTANCE,
            occurances: 0,
          };
        });
    const alredyExistObject = Alldata.find((dataKey) => dataKey.keyword == item.keyword);
    if (alredyExistObject) {
      if (comp_extracted_keywordsList && comp_extracted_keywordsList.length) {
        alredyExistObject.originalSynonymsData.push(...comp_extracted_keywordsList);
      }
      if (keyword_related_termsList && keyword_related_termsList.length) {
        alredyExistObject.originalRelatedTermData?.push(...keyword_related_termsList);
      }
    } else {
      Alldata.push({
        keyword: item.keyword,
        originalSynonymsData: comp_extracted_keywordsList
          ? comp_extracted_keywordsList.length
            ? comp_extracted_keywordsList
            : []
          : [],
        originalRelatedTermData: keyword_related_termsList
          ? keyword_related_termsList.length
            ? keyword_related_termsList
            : []
          : [],
      });
    }
  });

  const finalList = [] as any[];
  Alldata.forEach((item) => {
    finalList.push({
      keyword: item.keyword,
      originalSynonymsData:
        item && item.originalSynonymsData
          ? item.originalSynonymsData.filter((v, i, a) => a.findIndex((t) => t.term === v.term) === i).slice(0, 20)
          : [],
      originalRelatedTermData:
        item && item.originalRelatedTermData
          ? item.originalRelatedTermData.filter((v, i, a) => a.findIndex((t) => t.term === v.term) === i).slice(0, 20)
          : [],
    });
  });
  return finalList;
};

export const dummyNewFinalResponse = {
  data: {
    ksmApiResponse: {
      data: [
        {
          KEYWORD: 'online pharmacy',
          kes: 54,
          tag_score: {
            H1: 63,
            P: 20,
          },
          tag_group_score: {
            META: 301,
            BODY: 302,
          },
          element_score: {
            7246067621780113: 803,
            7911850679417835: 804,
          },
        },
        {
          KEYWORD: 'pocketpills',
          kes: 44,
          tag_score: {
            H1: 63,
            P: 20,
          },
          tag_group_score: {
            META: 401,
            BODY: 402,
          },
          element_score: {
            7246067621780113: 403,
            7911850679417835: 404,
          },
        },
        {
          KEYWORD: 'shoppers drug mart calgary',
          kes: 40,
          tag_score: {
            H1: 63,
            P: 20,
          },
          tag_group_score: {
            META: 501,
            BODY: 502,
          },
          element_score: {
            7246067621780113: 503,
            7911850679417835: 504,
          },
        },
        {
          KEYWORD: 'shoppers drug mart refill',
          kes: 42,
          tag_score: {
            H1: 63,
            P: 20,
          },
          tag_group_score: {
            META: 601,
            BODY: 602,
          },
          element_score: {
            7246067621780113: 603,
            7911850679417835: 604,
          },
        },
        {
          KEYWORD: 'shoppers drug mart',
          kes: 41,
          tag_score: {
            H1: 63,
            P: 20,
          },
          tag_group_score: {
            META: 701,
            BODY: 702,
          },
          element_score: {
            7246067621780113: 503,
            7911850679417835: 504,
          },
        },
        {
          KEYWORD: 'pocketpills pharmacy',
          kes: 60,
          tag_score: {
            H1: 63,
            P: 20,
          },
          tag_group_score: {
            META: 501,
            BODY: 502,
          },
          element_score: {
            7246067621780113: 503,
            7911850679417835: 504,
          },
        },
        {
          KEYWORD: 'goodrx canada',
          kes: 25,
          tag_score: {
            H1: 63,
            P: 20,
          },
          tag_group_score: {
            META: 501,
            BODY: 502,
          },
          element_score: {
            7246067621780113: 503,
            7911850679417835: 504,
          },
        },
        {
          KEYWORD: 'shoppers drug mart pharmacy',
          kes: 49,
          tag_score: {
            H1: 63,
            P: 20,
          },
          tag_group_score: {
            META: 501,
            BODY: 502,
          },
          element_score: {
            7246067621780113: 503,
            7911850679417835: 504,
          },
        },
        {
          KEYWORD: 'pharmacy delivery',
          kes: 49,
          tag_score: {
            H1: 63,
            P: 20,
          },
          tag_group_score: {
            META: 501,
            BODY: 502,
          },
          element_score: {
            7246067621780113: 503,
            7911850679417835: 504,
          },
        },
        {
          KEYWORD: 'online prescription',
          kes: 54,
          tag_score: {
            H1: 63,
            P: 20,
          },
          tag_group_score: {
            META: 501,
            BODY: 502,
          },
          element_score: {
            7246067621780113: 503,
            7911850679417835: 504,
          },
        },
        {
          KEYWORD: 'how to get birth control',
          kes: 39,
          tag_score: {
            H1: 63,
            P: 20,
          },
          tag_group_score: {
            META: 501,
            BODY: 502,
          },
          element_score: {
            7246067621780113: 503,
            7911850679417835: 504,
          },
        },
        {
          KEYWORD: 'pocketpills reviews',
          kes: 46,
          tag_score: {
            H1: 63,
            P: 20,
          },
          tag_group_score: {
            META: 501,
            BODY: 502,
          },
          element_score: {
            7246067621780113: 503,
            7911850679417835: 504,
          },
        },
        {
          KEYWORD: 'shoppers online refill',
          kes: 43,
          tag_score: {
            H1: 63,
            P: 20,
          },
          tag_group_score: {
            META: 501,
            BODY: 502,
          },
          element_score: {
            7246067621780113: 503,
            7911850679417835: 504,
          },
        },
        {
          KEYWORD: 'pill pockets',
          kes: 39,
          tag_score: {
            H1: 63,
            P: 20,
          },
          tag_group_score: {
            META: 501,
            BODY: 502,
          },
          element_score: {
            7246067621780113: 503,
            7911850679417835: 504,
          },
        },
        {
          KEYWORD: 'pocketpills mississauga',
          kes: 43,
          tag_score: {
            H1: 63,
            P: 20,
          },
          tag_group_score: {
            META: 501,
            BODY: 502,
          },
          element_score: {
            7246067621780113: 503,
            7911850679417835: 504,
          },
        },
      ],
      projected_kas: 145,
      projected_branded_kas: 146,
      projected_non_branded_kas: 145,
      overall_tag_group_score: {
        META: 101,
        BODY: 102,
      },
      overall_element_score: {
        7246067621780113: 801,
        7911850679417835: 802,
      },
    },
    contentQualityResponse: {
      CONTENT_QUALITY: {
        'goodrx canada': {
          'https://www.canadianhealthcarenetwork.ca/amazon-pharmacy-coming-will-you-be-ready': 0,
          'https://www.goodrx.com/': 0,
          'https://www.pillpack.com/': 0,
          'https://www.pocketpills.com/': 100,
          'https://www.theglobeandmail.com/amp/globe-investor/personal-finance/household-finances/four-ways-canadians-can-save-money-on-prescription-meds/article33825086/': 0,
          'https://www.wzzm13.com/article/news/investigations/13-on-your-side/the-canadian-remedy-how-to-save-hundreds-on-prescription-drugs/407942367': 0,
        },
        'how to get birth control': {
          'https://kidshealth.org/en/teens/contraception-birth.html': 0,
          'https://myhealth.alberta.ca/Health/pages/conditions.aspx?hwid=hw237864': 0,
          'https://www.felixforyou.ca/ailment/birth-control': 0,
          'https://www.mayoclinic.org/healthy-lifestyle/birth-control/in-depth/best-birth-control-pill/art-20044807': 0,
          'https://www.plannedparenthood.org/learn/birth-control/birth-control-pill/how-do-i-get-birth-control-pills': 0,
          'https://www.pocketpills.com/': 100,
        },
        'online pharmacy': {
          'https://mednow.ca/': 0,
          'https://napra.ca/online-pharmacies': 71.4,
          'https://www.canada.ca/en/health-canada/topics/buying-using-drug-health-products-safely/safe-use-online-pharmacies.html': 0,
          'https://www.csmedcenter.com/': 0,
          'https://www.pharmexdirect.com/': 0,
          'https://www.pocketpills.com/': 100,
        },
        'online prescription': {
          'https://mypharmacy.shoppersdrugmart.ca/en': 0,
          'https://pharmasave.com/prescriptions/': 0,
          'https://tiahealth.com/online-medical-services/online-prescription/': 100,
          'https://www.felixforyou.ca/': 0,
          'https://www.getmaple.ca/features/online-prescriptions/': 65.4,
          'https://www.pocketpills.com/': 50.7,
        },
        'page-level': {
          'https://birdeye.com/pocketpills-pharmacy-158828848455228': 59,
          'https://kidshealth.org/en/teens/contraception-birth.html': 0,
          'https://luminohealth.sunlife.ca/en/health-care-facility-profile/pharmacy/pocketpills-778347/': 59,
          'https://mypharmacy.shoppersdrugmart.ca/en': 0,
          'https://napra.ca/online-pharmacies': 60.2,
          'https://stores.shoppersdrugmart.ca/': 10.7,
          'https://techcrunch.com/2020/02/19/canadian-online-pharmacy-pocketpills-has-raised-7-35-million-as-it-expands-into-quebec/': 34.3,
          'https://tiahealth.com/online-medical-services/online-prescription/': 100,
          'https://www.amazon.ca/Greenies-Pill-Pockets-Treats-Dogs/dp/B00HQM0UMS': 0,
          'https://www.businesswire.com/news/home/20210303005134/en/PocketPills-Raises-30M-in-Series-B-Funding-to-Fuel-Same-Day-Delivery-Nationwide': 0,
          'https://www.centralwesthealthline.ca/displayservice.aspx?id=199105': 0,
          'https://www.getmaple.ca/features/online-prescriptions/': 62.7,
          'https://www.glassdoor.ca/Overview/Working-at-PocketPills-EI_IE2253999.11,22.htm': 0,
          'https://www.goodrx.com/': 0,
          'https://www.greenies.com/products/dog/pill-pockets': 0,
          'https://www.pillpack.com/': 65.8,
          'https://www.plannedparenthood.org/learn/birth-control/birth-control-pill/how-do-i-get-birth-control-pills': 0,
          'https://www.pocketpills.com/': 81.6,
          'https://www1.shoppersdrugmart.ca/en/health-and-pharmacy/pharmacy-services': 32.8,
          'https://www1.shoppersdrugmart.ca/en/home': 61.3,
          'https://www1.shoppersdrugmart.ca/en/refill-prescriptions': 0,
        },
        'pharmacy delivery': {
          'https://www.pocketpills.com/': 100,
        },
        'pill pockets': null,
        pocketpills: null,
        'pocketpills mississauga': null,
        'pocketpills pharmacy': null,
        'pocketpills reviews': null,
        'shoppers drug mart': {
          'https://loblaw.force.com/shoppersdrugmart/s/': 0,
          'https://shop.wellwise.ca/': 0,
          'https://stores.shoppersdrugmart.ca/': 16.7,
          'https://www.federalrapidscreening.morewaystobenefit.ca/': 0,
          'https://www.pocketpills.com/': 64.7,
          'https://www1.shoppersdrugmart.ca/en/home': 100,
        },
        'shoppers drug mart calgary': {
          'https://www.pocketpills.com/': 100,
        },
        'shoppers drug mart pharmacy': {
          'https://covid-19.ontario.ca/vaccine-locations': 0,
          'https://mypharmacy.shoppersdrugmart.ca/en': 0,
          'https://toronto.ctvnews.ca/ontario-pharmacist-quits-job-in-protest-over-shoppers-drug-mart-s-symptomatic-covid-19-testing-plan-1.5708060': 0,
          'https://www.cp24.com/news/toronto-pharmacist-quits-job-in-protest-over-shoppers-drug-mart-symptomatic-covid-19-testing-plan-1.5708010': 0,
          'https://www.pocketpills.com/': 100,
          'https://www1.shoppersdrugmart.ca/en/health-and-pharmacy/pharmacy-services': 50.2,
        },
        'shoppers drug mart refill': {
          'https://loblaw.force.com/shoppersdrugmart/s/article/Where-can-I-get-prescriptions-refilled-or-transferred': 0,
          'https://mypharmacy.shoppersdrugmart.ca/en': 0,
          'https://rxrefills.londondrugs.com/': 0,
          'https://www.pocketpills.com/': 100,
          'https://www.sickkids.ca/en/patients-visitors/pharmacy-prescriptions/': 0,
          'https://www1.shoppersdrugmart.ca/en/refill-prescriptions': 0,
        },
        'shoppers online refill': {
          'https://www.pocketpills.com/': 100,
        },
      },
      TOPICS: [
        {
          COMPETITOR_PREVALENCE: 1,
          KEYWORD_INFO: [
            {
              KEYWORD: 'online pharmacy',
              KEYWORD_IMPORTANCE: 100,
              KEYWORD_RELEVANCE: 0.88,
            },
            {
              KEYWORD: 'online prescription',
              KEYWORD_IMPORTANCE: 16,
              KEYWORD_RELEVANCE: 0.63,
            },
            {
              KEYWORD: 'pharmacy delivery',
              KEYWORD_IMPORTANCE: 16,
              KEYWORD_RELEVANCE: 0.6,
            },
            {
              KEYWORD: 'shoppers online refill',
              KEYWORD_IMPORTANCE: 14,
              KEYWORD_RELEVANCE: 0.52,
            },
            {
              KEYWORD: 'shoppers drug mart pharmacy',
              KEYWORD_IMPORTANCE: 23,
              KEYWORD_RELEVANCE: 0.5,
            },
            {
              KEYWORD: 'shoppers drug mart',
              KEYWORD_IMPORTANCE: 29,
              KEYWORD_RELEVANCE: 0.34,
            },
            {
              KEYWORD: 'shoppers drug mart calgary',
              KEYWORD_IMPORTANCE: 63,
              KEYWORD_RELEVANCE: 0.33,
            },
            {
              KEYWORD: 'goodrx canada',
              KEYWORD_IMPORTANCE: 24,
              KEYWORD_RELEVANCE: 0.3,
            },
            {
              KEYWORD: 'shoppers drug mart refill',
              KEYWORD_IMPORTANCE: 42,
              KEYWORD_RELEVANCE: 0.28,
            },
            {
              KEYWORD: 'how to get birth control',
              KEYWORD_IMPORTANCE: 16,
              KEYWORD_RELEVANCE: 0,
            },
          ],
          NUM_SENTS: 10,
          NUM_SENTS_COMPETITORS: 1,
          NUM_SENTS_CONTENT: {
            709185847191614: 2,
            3074676621614107: 5,
            7246067621780113: 2,
          },
          NUM_SENTS_CUSTOMER: 9,
          TOPIC_ID: 'c14d4df8-cce5-4a92-842f-20b3c8d192b8',
          TOPIC_IMPORTANCE: {
            'goodrx canada': 100,
            'online pharmacy': 100,
            'online prescription': 57.8,
            'page-level': 100,
            'pharmacy delivery': 100,
            'shoppers drug mart': 23.7,
            'shoppers drug mart calgary': 100,
            'shoppers drug mart pharmacy': 73.3,
            'shoppers drug mart refill': null,
            'shoppers online refill': 100,
          },
          TOPIC_IMPORTANCE_RAW: {
            'goodrx canada': 7.199999999999999,
            'online pharmacy': 88,
            'online prescription': 10.08,
            'page-level': 51.4,
            'pharmacy delivery': 9.6,
            'shoppers drug mart': 9.860000000000001,
            'shoppers drug mart calgary': 20.790000000000003,
            'shoppers drug mart pharmacy': 11.5,
            'shoppers drug mart refill': 11.760000000000002,
            'shoppers online refill': 7.28,
          },
        },
      ],
      URL: 'https://www.pocketpills.com/',
    },
    formattedScores: {
      elementsScores: {
        7246067621780113: {
          keywordWiseElementScore: [
            {
              keyword: 'online pharmacy',
              score: 803,
            },
            {
              keyword: 'pocketpills',
              score: 403,
            },
            {
              keyword: 'shoppers drug mart calgary',
              score: 503,
            },
            {
              keyword: 'shoppers drug mart refill',
              score: 603,
            },
            {
              keyword: 'shoppers drug mart',
              score: 503,
            },
            {
              keyword: 'pocketpills pharmacy',
              score: 503,
            },
            {
              keyword: 'goodrx canada',
              score: 503,
            },
            {
              keyword: 'shoppers drug mart pharmacy',
              score: 503,
            },
            {
              keyword: 'pharmacy delivery',
              score: 503,
            },
            {
              keyword: 'online prescription',
              score: 503,
            },
            {
              keyword: 'how to get birth control',
              score: 503,
            },
            {
              keyword: 'pocketpills reviews',
              score: 503,
            },
            {
              keyword: 'shoppers online refill',
              score: 503,
            },
            {
              keyword: 'pill pockets',
              score: 503,
            },
            {
              keyword: 'pocketpills mississauga',
              score: 503,
            },
          ],
          overallElementScore: 801,
        },
        7911850679417835: {
          keywordWiseElementScore: [
            {
              keyword: 'online pharmacy',
              score: 804,
            },
            {
              keyword: 'pocketpills',
              score: 404,
            },
            {
              keyword: 'shoppers drug mart calgary',
              score: 504,
            },
            {
              keyword: 'shoppers drug mart refill',
              score: 604,
            },
            {
              keyword: 'shoppers drug mart',
              score: 504,
            },
            {
              keyword: 'pocketpills pharmacy',
              score: 504,
            },
            {
              keyword: 'goodrx canada',
              score: 504,
            },
            {
              keyword: 'shoppers drug mart pharmacy',
              score: 504,
            },
            {
              keyword: 'pharmacy delivery',
              score: 504,
            },
            {
              keyword: 'online prescription',
              score: 504,
            },
            {
              keyword: 'how to get birth control',
              score: 504,
            },
            {
              keyword: 'pocketpills reviews',
              score: 504,
            },
            {
              keyword: 'shoppers online refill',
              score: 504,
            },
            {
              keyword: 'pill pockets',
              score: 504,
            },
            {
              keyword: 'pocketpills mississauga',
              score: 504,
            },
          ],
          overallElementScore: 802,
        },
      },
      tagScore: [
        {
          tag: 'H1',
          score: 63,
          keyword: 'online pharmacy',
        },
        {
          tag: 'P',
          score: 20,
          keyword: 'online pharmacy',
        },
        {
          tag: 'H1',
          score: 63,
          keyword: 'pocketpills',
        },
        {
          tag: 'P',
          score: 20,
          keyword: 'pocketpills',
        },
        {
          tag: 'H1',
          score: 63,
          keyword: 'shoppers drug mart calgary',
        },
        {
          tag: 'P',
          score: 20,
          keyword: 'shoppers drug mart calgary',
        },
        {
          tag: 'H1',
          score: 63,
          keyword: 'shoppers drug mart refill',
        },
        {
          tag: 'P',
          score: 20,
          keyword: 'shoppers drug mart refill',
        },
        {
          tag: 'H1',
          score: 63,
          keyword: 'shoppers drug mart',
        },
        {
          tag: 'P',
          score: 20,
          keyword: 'shoppers drug mart',
        },
        {
          tag: 'H1',
          score: 63,
          keyword: 'pocketpills pharmacy',
        },
        {
          tag: 'P',
          score: 20,
          keyword: 'pocketpills pharmacy',
        },
        {
          tag: 'H1',
          score: 63,
          keyword: 'goodrx canada',
        },
        {
          tag: 'P',
          score: 20,
          keyword: 'goodrx canada',
        },
        {
          tag: 'H1',
          score: 63,
          keyword: 'shoppers drug mart pharmacy',
        },
        {
          tag: 'P',
          score: 20,
          keyword: 'shoppers drug mart pharmacy',
        },
        {
          tag: 'H1',
          score: 63,
          keyword: 'pharmacy delivery',
        },
        {
          tag: 'P',
          score: 20,
          keyword: 'pharmacy delivery',
        },
        {
          tag: 'H1',
          score: 63,
          keyword: 'online prescription',
        },
        {
          tag: 'P',
          score: 20,
          keyword: 'online prescription',
        },
        {
          tag: 'H1',
          score: 63,
          keyword: 'how to get birth control',
        },
        {
          tag: 'P',
          score: 20,
          keyword: 'how to get birth control',
        },
        {
          tag: 'H1',
          score: 63,
          keyword: 'pocketpills reviews',
        },
        {
          tag: 'P',
          score: 20,
          keyword: 'pocketpills reviews',
        },
        {
          tag: 'H1',
          score: 63,
          keyword: 'shoppers online refill',
        },
        {
          tag: 'P',
          score: 20,
          keyword: 'shoppers online refill',
        },
        {
          tag: 'H1',
          score: 63,
          keyword: 'pill pockets',
        },
        {
          tag: 'P',
          score: 20,
          keyword: 'pill pockets',
        },
        {
          tag: 'H1',
          score: 63,
          keyword: 'pocketpills mississauga',
        },
        {
          tag: 'P',
          score: 20,
          keyword: 'pocketpills mississauga',
        },
      ],
      tagGroupScore: [
        {
          score: 301,
          keyword: 'online pharmacy',
          'tag-group': 'META',
        },
        {
          score: 302,
          keyword: 'online pharmacy',
          'tag-group': 'BODY',
        },
        {
          score: 401,
          keyword: 'pocketpills',
          'tag-group': 'META',
        },
        {
          score: 402,
          keyword: 'pocketpills',
          'tag-group': 'BODY',
        },
        {
          score: 501,
          keyword: 'shoppers drug mart calgary',
          'tag-group': 'META',
        },
        {
          score: 502,
          keyword: 'shoppers drug mart calgary',
          'tag-group': 'BODY',
        },
        {
          score: 601,
          keyword: 'shoppers drug mart refill',
          'tag-group': 'META',
        },
        {
          score: 602,
          keyword: 'shoppers drug mart refill',
          'tag-group': 'BODY',
        },
        {
          score: 701,
          keyword: 'shoppers drug mart',
          'tag-group': 'META',
        },
        {
          score: 702,
          keyword: 'shoppers drug mart',
          'tag-group': 'BODY',
        },
        {
          score: 501,
          keyword: 'pocketpills pharmacy',
          'tag-group': 'META',
        },
        {
          score: 502,
          keyword: 'pocketpills pharmacy',
          'tag-group': 'BODY',
        },
        {
          score: 501,
          keyword: 'goodrx canada',
          'tag-group': 'META',
        },
        {
          score: 502,
          keyword: 'goodrx canada',
          'tag-group': 'BODY',
        },
        {
          score: 501,
          keyword: 'shoppers drug mart pharmacy',
          'tag-group': 'META',
        },
        {
          score: 502,
          keyword: 'shoppers drug mart pharmacy',
          'tag-group': 'BODY',
        },
        {
          score: 501,
          keyword: 'pharmacy delivery',
          'tag-group': 'META',
        },
        {
          score: 502,
          keyword: 'pharmacy delivery',
          'tag-group': 'BODY',
        },
        {
          score: 501,
          keyword: 'online prescription',
          'tag-group': 'META',
        },
        {
          score: 502,
          keyword: 'online prescription',
          'tag-group': 'BODY',
        },
        {
          score: 501,
          keyword: 'how to get birth control',
          'tag-group': 'META',
        },
        {
          score: 502,
          keyword: 'how to get birth control',
          'tag-group': 'BODY',
        },
        {
          score: 501,
          keyword: 'pocketpills reviews',
          'tag-group': 'META',
        },
        {
          score: 502,
          keyword: 'pocketpills reviews',
          'tag-group': 'BODY',
        },
        {
          score: 501,
          keyword: 'shoppers online refill',
          'tag-group': 'META',
        },
        {
          score: 502,
          keyword: 'shoppers online refill',
          'tag-group': 'BODY',
        },
        {
          score: 501,
          keyword: 'pill pockets',
          'tag-group': 'META',
        },
        {
          score: 502,
          keyword: 'pill pockets',
          'tag-group': 'BODY',
        },
        {
          score: 501,
          keyword: 'pocketpills mississauga',
          'tag-group': 'META',
        },
        {
          score: 502,
          keyword: 'pocketpills mississauga',
          'tag-group': 'BODY',
        },
      ],
      kesScore: [
        {
          score: 54,
          keyword: 'online pharmacy',
        },
        {
          score: 44,
          keyword: 'pocketpills',
        },
        {
          score: 40,
          keyword: 'shoppers drug mart calgary',
        },
        {
          score: 42,
          keyword: 'shoppers drug mart refill',
        },
        {
          score: 41,
          keyword: 'shoppers drug mart',
        },
        {
          score: 60,
          keyword: 'pocketpills pharmacy',
        },
        {
          score: 25,
          keyword: 'goodrx canada',
        },
        {
          score: 49,
          keyword: 'shoppers drug mart pharmacy',
        },
        {
          score: 49,
          keyword: 'pharmacy delivery',
        },
        {
          score: 54,
          keyword: 'online prescription',
        },
        {
          score: 39,
          keyword: 'how to get birth control',
        },
        {
          score: 46,
          keyword: 'pocketpills reviews',
        },
        {
          score: 43,
          keyword: 'shoppers online refill',
        },
        {
          score: 39,
          keyword: 'pill pockets',
        },
        {
          score: 43,
          keyword: 'pocketpills mississauga',
        },
      ],
      kasAlignmentScore: 145,
      brandedKas: 146,
      nonBrandedKas: 145,
    },
  },
  status: 'success',
  status_code: 200,
  message: 'Success',
};

export const searchForTopicId = (data, searchElementIdInTopic) => {
  let foundTopicId = '';
  data.forEach((item) => {
    item.CONTENT.forEach((record) => {
      if (record.html_id == searchElementIdInTopic) {
        foundTopicId = item.TOPIC_ID;
      }
    });
  });
  return foundTopicId;
};

export const searchForParentElement = (customerTabsData, childElementId) => {
  let parent;
  ['META', 'HEADINGS'].forEach((tag) => {
    if (customerTabsData[tag]?.length > 0) {
      customerTabsData[tag].forEach((item) => {
        item?.siblings?.forEach((record) => {
          if (record.id == childElementId) {
            parent = item;
          }
        });
      });
    }
  });
  return parent;
};

export const getHtmlElementScores = ({
  _storedKeywordsData,
  tabData,
  isCompetitor,
  tab,
  isForTopicDiscoveryTab = false,
  selectedImproveKeyword,
  selectedCompetitorName,
}) => {
  let updatedTabData = tabData;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let suggestedData: any = [];
  let competitorsHtmlElementScores, customerHtmlElementScores;

  const selectedCompetitorUrl = selectedCompetitorName; // selectedCompetitorName means url;

  const selectedKeywordDropdown = isForTopicDiscoveryTab ? undefined : selectedImproveKeyword;
  const keywordData = _storedKeywordsData.find(
    (data) => data?.keyword?.toLowerCase() === selectedKeywordDropdown?.toLowerCase() && data?.sub_keyword == null
  );
  if (keywordData) {
    competitorsHtmlElementScores = keywordData?.competitor_html_element_scores;
    customerHtmlElementScores = keywordData?.customer_html_element_scores;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let competitorHtmlScores: any = [];
  if (competitorsHtmlElementScores?.length) {
    competitorHtmlScores = competitorsHtmlElementScores?.find(
      (compData) => compData?.competitor_page?.toLowerCase() === selectedCompetitorUrl?.toLowerCase()
    );
  }
  if (competitorsHtmlElementScores?.length && isForTopicDiscoveryTab) {
    competitorHtmlScores = { scores: [] };
    competitorsHtmlElementScores.forEach(
      (obj) => (competitorHtmlScores.scores = [...(competitorHtmlScores.scores || []), ...obj.scores])
    );
  }
  if (!isCompetitor && customerHtmlElementScores && customerHtmlElementScores?.scores) {
    updatedTabData = [];
    suggestedData = [];
    tabData.forEach((data) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let htmlScore: any = null;

      if (isForTopicDiscoveryTab && data?.siblings && data?.siblings?.length) {
        data.siblings.forEach((siblingsData) => {
          htmlScore = customerHtmlElementScores?.scores?.find((custData) => custData?.element_id === siblingsData?.id);
          siblingsData.htmlElementScore = htmlScore ? htmlScore?.element_hf_score : -1;
          siblingsData.projectedHtmlElementScore = (htmlScore && htmlScore?.projected_score) || null;
        });
      }

      htmlScore = customerHtmlElementScores?.scores.find((custData) => custData?.element_id === data.id);
      if (data.isSuggested) {
        let obj = { ...data };
        if (htmlScore) {
          obj = {
            ...obj,
            htmlElementScore: htmlScore?.element_hf_score,
            projectedHtmlElementScore: htmlScore?.projected_score,
          };
        }
        suggestedData.push({ ...obj });
      } else {
        if (htmlScore) {
          updatedTabData.push({
            ...data,
            htmlElementScore: htmlScore?.element_hf_score,
            projectedHtmlElementScore: htmlScore?.projected_score,
          });
        } else {
          updatedTabData.push({ ...data, htmlElementScore: -1 });
        }
      }
    });
    if (['STRUCTURED DATA', 'META'].includes(tab)) {
      return [...suggestedData, ...updatedTabData];
    } else {
      return [...suggestedData, ...updatedTabData.sort((a, b) => b?.htmlElementScore - a?.htmlElementScore)];
    }
  }

  if (isCompetitor && competitorHtmlScores && competitorHtmlScores.scores) {
    updatedTabData = [];
    tabData.forEach((data) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let htmlScore: any = null;

      if (isForTopicDiscoveryTab && data?.siblings && data?.siblings.length) {
        data.siblings.forEach((siblingsData) => {
          htmlScore = competitorHtmlScores.scores.find((custData) => custData?.element_id === siblingsData?.id);
          siblingsData.htmlElementScore = htmlScore ? htmlScore?.element_hf_score : -1;
          siblingsData.projectedHtmlElementScore = htmlScore ? htmlScore?.projected_score : null;
        });
      }

      htmlScore = competitorHtmlScores?.scores.find((compData) => compData?.element_id === data.id);
      if (htmlScore) {
        updatedTabData.push({ ...data, htmlElementScore: htmlScore?.element_hf_score });
      } else {
        updatedTabData.push({ ...data, htmlElementScore: -1 });
      }
    });
    return tab === 'STRUCTURED DATA'
      ? updatedTabData
      : updatedTabData.sort((a, b) => b?.htmlElementScore - a?.htmlElementScore);
  }
  return tabData;
};

export const delayTimer = (delayInms = 1000) => {
  return new Promise((resolve) => setTimeout(resolve, delayInms));
};

export const getStreamingGptInstructionPayload = ({
  text,
  tag,
  pageId,
  pageUrl,
  isPlainText = true,
  keywordHierarchy,
}: {
  text: string;
  tag: string;
  pageId: string;
  pageUrl: string;
  isPlainText?: boolean;
  keywordHierarchy?: string;
}) => {
  const selectedTag = tag?.toUpperCase();

  const tagLabel = getTagLabel(selectedTag || 'text');
  let tagLabelText = (tagLabel?.label || '')
    .toLowerCase()
    ?.replace(/(<([^>]+)>)/gi, '')
    ?.replace(/-/g, '')
    ?.replace(/\s/g, '');
  tagLabelText = tagLabelText ? `${tagLabelText} text` : 'text';

  // let prompt = `Rewrite a ${tagLabelText} similar to "${text}"`;

  // prompt += isPlainText
  //   ? `\n Note: return only one ${tagLabelText}. output should be strictly in text format without any html tags. Do not add any quotes around the text`
  //   : `\n Note: return only one ${tagLabelText}. output should be strictly in HTML format. Do not add any quotes around the text`;
  return {
    promptFor: 'OPTIMIZE_CONTENT.REWRITE_SIMILAR_CONTENT',
    promptConfig: {
      format: isPlainText ? 'text format without any html tags' : 'HTML format',
      tagLabelText,
      text,
      usePageContext: true,
      tag: selectedTag,
      keywordHierarchy,
      pageId,
      pageUrl,
    },
    instruction: '',
    tag: selectedTag,
    max_tokens: 3500,
  };
};

export const closeWsInstance = () => {
  const wsInstance = APP_STORE?.getState()?.root?.wsInstance;
  if (wsInstance && wsInstance?.close) {
    wsInstance.close();
    console.log('Cr-commoin:: closeWsInstance :: ws instance closed');
  }
};

export const getNewNextHeading = (previousHeadingTag = 'H1') => {
  const headingTags = ['h2', 'h3', 'h4', 'h5', 'h6'];
  const previousIndex = headingTags.indexOf(previousHeadingTag.toLowerCase()) || 0;
  return headingTags?.[previousIndex + 1] || previousHeadingTag; // Return the next heading tag in the array.
};

export const getIncludedExcludedAndPinnedUnpinned = ({ cikData, cpkData, aiUpdatedData }) => {
  const cikIncludedClusterId: any = [];
  const cikExcludedClusterId: any = [];

  const cpkIncludedClusterId: any = [];
  const cpkExcludedClusterId: any = [];

  for (const aiData of aiUpdatedData) {
    if (!aiData?.is_cluster_included && aiData?.is_included && !aiData?.sub_keyword) {
      cikIncludedClusterId.push(aiData);
    }
    if (aiData?.is_cluster_included && !aiData?.is_included && !aiData?.sub_keyword) {
      cikExcludedClusterId.push(aiData);
    }
  }

  for (const cpkD of cpkData) {
    if (!cpkD?.is_cluster_included && cpkD?.is_included && !cpkD?.sub_keyword) {
      cpkIncludedClusterId.push(cpkD);
    }
    if (cpkD?.is_cluster_included && !cpkD?.is_included && !cpkD?.sub_keyword) {
      cpkExcludedClusterId.push(cpkD);
    }
  }

  return {
    cikIncludedClusterId: cikIncludedClusterId?.map((c) => c?.cluster_id),
    cikExcludedClusterId: cikExcludedClusterId?.map((c) => c?.cluster_id),
    cpkIncludedClusterId: cpkIncludedClusterId?.map((c) => c?.cluster_id),
    cpkExcludedClusterId: cpkExcludedClusterId?.map((c) => c?.cluster_id),
  };
};

// export const getKeywordListForDropdownOnly = (
//   keywordsData: typeKeywordDetails[] = [],
//   isBrandedIncluded = true,
//   isOnlyProcessKeywords = true
// ) => {
//   return keywordsData?.filter(
//     (c: any) =>
//       c?.is_included &&
//       (isOnlyProcessKeywords ? c?.is_cluster_included : true) &&
//       (isBrandedIncluded ? c?.is_branded === true || c?.is_branded === false : c?.is_branded === false)
//   );
// };

export const getKeywordListForDropdownOnly = (
  keywordsData: typeKeywordDetails[] = [],
  isBrandedIncluded = true,
  isOnlyProcessKeywords = true
) => {
  const parents = keywordsData.filter(
    (c: any) =>
      c?.is_included &&
      !c?.sub_keyword &&
      (isOnlyProcessKeywords ? c?.is_cluster_included : true) &&
      (isBrandedIncluded ? c?.is_branded === true || c?.is_branded === false : c?.is_branded === false)
  );

  return parents.flatMap((parent: any) => [
    parent,
    ...keywordsData.filter((c: any) => c?.sub_keyword && c?.cluster_id === parent?.cluster_id),
  ]);
};

export const changeStatusForPageHandler = async ({
  date,
  pageId,
  reason,
  annotationSubtype = 'selected value',
  isFromContentBriefScreen,
}: {
  date?: Date;
  pageId: string;
  reason?: string;
  annotationSubtype: string;
  isFromContentBriefScreen?: boolean;
}) => {
  const dateValue = date || new Date();
  const payload = {
    recommendation_id: pageId,
    reason: reason || annotationSubtype,
    date: format(new Date(dateValue), 'MM/dd/yyyy'),
    entity_type: isFromContentBriefScreen
      ? _RESOLUTION_ENTITY_TYPE.CNP
      : _RESOLUTION_ENTITY_TYPE.CONTENT_RECOMMENDATIONS,
    entity_id: pageId,
    annotation_type: isFromContentBriefScreen ? ANNOTATION_TYPE.PUBLISHED : ANNOTATION_TYPE.RESOLUTION,
    annotation_subtype: annotationSubtype,
    annotation_details: {},
  };
  try {
    const result = await ApiService.post(`${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/utility/annotation`, payload);
    const output = { ...(result?.data || {}), reason: reason || annotationSubtype };
    return output;
  } catch (error) {
    console.error('changeStatusForPageHandler:: error', error);
  }
};

export const fetchPageLevelSummary = async ({
  selected_schema,
  id,
  viewName = 'content_recommendations',
  isForRefetch = false,
}) => {
  const fieldsOnlyForRefetch = [
    `${viewName}.insight`,
    `${viewName}.projected_keyword_alignment_score`,
    `${viewName}.keyword_alignment_score`,
    `${viewName}.row_id`,
    `${viewName}.non_branded_keyword_alignment_score`,
    `${viewName}.overall_element_scores`,
    `${viewName}.overall_tag_group_scores`,
    `${viewName}.is_page_processed`,
  ];

  const fields = [
    `${viewName}.insight`,
    `${viewName}.projected_keyword_alignment_score`,
    `${viewName}.keyword_alignment_score`,
    `${viewName}.row_id`,
    `${viewName}.monthly_clicks`,
    `${viewName}.monthly_impressions`,
    `${viewName}.finalurl`,
    `${viewName}.unique_keywords`,
    `${viewName}.data_source`,
    `${viewName}.paid_monthly_clicks`,
    `${viewName}.paid_monthly_impressions`,
    `${viewName}.non_branded_keyword_alignment_score`,
    `${viewName}.update_date`,
    `${viewName}.overall_element_scores`,
    `${viewName}.overall_tag_group_scores`,
    `${viewName}.country`,
    `${viewName}.language`,
    `${viewName}.is_sandbox_data`,
    `${viewName}.is_page_processed`,
  ];

  const payload = {
    model: process.env.NEXT_PUBLIC_LOOKER_MODEL_NAME,
    view: viewName,
    fields: isForRefetch ? fieldsOnlyForRefetch : fields,
    filters: {
      [`${viewName}.uuid`]: id,
    },
  };
  const result = await ApiService.post('summary_panel', { selected_schema, params: payload });
  return result;
};

const arraysToSet = (array: any[]): Set<string> => {
  const set = new Set<string>();
  for (const obj of array) {
    const str = JSON.stringify(obj);
    set.add(str);
  }
  return set;
};

export const isBothArrayIsSame = (A: any[], B: any[]): boolean => {
  if ((!A || A.length === 0) && B && B.length > 0) {
    return false;
  }
  if ((!B || B.length === 0) && A && A.length > 0) {
    return false;
  }

  // Convert arrays to sets of stringified key-value pairs
  const setA = arraysToSet(A);
  const setB = arraysToSet(B);

  // Check if sets are different
  return isEqual(setA, setB);
};

// Helper function to compare sets
const isEqual = (setA: Set<string>, setB: Set<string>): boolean => {
  if (setA.size !== setB.size) {
    return false;
  }
  for (const item of setA) {
    if (!setB.has(item)) {
      return false;
    }
  }
  return true;
};

export const acceptOrRejectAutoWriteOutputHandler = (payload) => {
  try {
    ApiService.post(`${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/utility/auto-write-accept-reject-logs`, {
      payload,
    });

    const eventName = payload?.isAccept ? `Auto Write Accept` : `Auto Write Reject`;
    trackAnalyticsEvent({
      eventFor: '',
      buttonText: ``,
      eventName: eventName,
      payload: {
        label: '',
        event_name: eventName,
        customer_url: payload?.pageUrl,
        page_id: payload?.pageId,
        ...payload,
      },
    });
  } catch (error) {
    console.error('CR-Common:: acceptOrRejectAutoWriteOutputHandler:: error', error);
  }
};

export const convertObjectKeysToLowerCase = (obj) => {
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
    return obj;
  }

  return Object.keys(obj).reduce((acc, key) => {
    const lowerCaseKey = key.toLowerCase();
    acc[lowerCaseKey] = obj[key];
    return acc;
  }, {});
};

///// Replaceed with new i.e jitCompHtmlLoadingUpdateHandler
// export const updateCompJitLoadingHandler = (originalJitLoading, keyword, compUrl, isScoreLoading = false) => {
//   if (!isScoreLoading) {
//     console.log('originalJitLoading', originalJitLoading);
//   }
//   let allExistingJitLoadingData = { ...(originalJitLoading || {}) };
//   if (allExistingJitLoadingData?.keywordWiseCompetitors) {
//     const keywordWisCompListObject = convertObjectKeysToLowerCase(
//       allExistingJitLoadingData?.keywordWiseCompetitors || {}
//     );
//     const entryForKeywordWiseLoading = keywordWisCompListObject?.[keyword?.toLowerCase()] || [];
//     if (!entryForKeywordWiseLoading?.length) {
//       allExistingJitLoadingData.keywordWiseCompetitors[keyword] = [
//         {
//           url: compUrl,
//           isScoreLoading,
//         },
//       ];
//     } else {
//       const filterKeywordArray = entryForKeywordWiseLoading?.filter((obj) => obj?.url !== compUrl) || [];
//       filterKeywordArray.push({
//         url: compUrl,
//         isScoreLoading,
//       });
//       allExistingJitLoadingData.keywordWiseCompetitors[keyword] = filterKeywordArray;
//     }
//   } else {
//     allExistingJitLoadingData = {
//       ...allExistingJitLoadingData,
//       keywordWiseCompetitors: {
//         [keyword]: [
//           {
//             url: compUrl,
//             isScoreLoading: true,
//           },
//         ],
//       },
//     };
//   }
//   return allExistingJitLoadingData;
// };

export const jitCompHtmlLoadingUpdateHandler = (originalState, compUrl, isLoading) => {
  const allExistingJitLoadingData = { ...(originalState || {}) };
  if (isLoading) {
    allExistingJitLoadingData?.loadingCompetitorHtmlData?.push(compUrl);
  } else {
    allExistingJitLoadingData.loadingCompetitorHtmlData = allExistingJitLoadingData?.loadingCompetitorHtmlData?.filter(
      (url) => url !== compUrl
    );
  }
  return allExistingJitLoadingData;
};

export const COMPETITOR_PAGE_TYPES = [
  'Product Details Page',
  'Search Results Page',
  'Landing Page',
  'Home Page',
  'Blog',
  'Glossary',
  'Listicle',
  'News Article',
  'Forum Posts',
  'How to Guides & KB',
  'Category',
  'Other',
];

export const COMPETITOR_DOMAIN_TYPES = [
  'eCommerce Site',
  'Affiliate Site',
  'Blog',
  'Corporate B2B Website',
  'Corporate B2C Website',
  'Aggregator',
  'User-Generated Content (UGC) Site',
  'Social Media Site',
  'App Store',
  'Video Website',
  'News Website',
  'Educational Site',
  'Government Website',
  'Non-profit Website',
  'Portal Site',
  'Forum',
  'Entertainment Website',
  'Review Site',
  'Directory Site',
  'Personal Website',
];

export const decodeHtmlText = (html) => {
  const uniqueId = `temp-decode-html-text`;
  const div = document.createElement('div');
  div.id = uniqueId;
  div.innerHTML = html;
  document.body.appendChild(div);
  const decodedText = div.textContent || div.innerText;
  document.body.removeChild(div);
  return decodedText;
};

export const handlePlainTextPaste = (event) => {
  event.preventDefault();
  const plainText = event?.clipboardData?.getData('text/plain');
  document.execCommand('insertText', false, plainText);
};

// export const getMappedTopicsContentAndImprovedContent = (storedTopicsData, storedPageAlHtmlData) => {
//   try {
//     // Create map for quick lookup
//     const htmlDataMap = new Map();
//     storedPageAlHtmlData.forEach(item => {
//       htmlDataMap.set(item.id, {
//         text: item.text,
//         siblings: item.siblings || []
//       });
//     });

//     // Deep clone the data
//     const updatedData = JSON.parse(JSON.stringify(storedTopicsData));

//     // Recursive function to traverse and update all nested objects
//     const updateNestedObjects = (obj) => {
//       if (!obj || typeof obj !== 'object') {
//         return obj;
//       }

//       // If it's an array, map through it
//       if (Array.isArray(obj)) {
//         return obj.map(item => updateNestedObjects(item));
//       }

//       // Check if current object has html_id and update it
//       if (obj.html_id && htmlDataMap.has(obj.html_id)) {
//         const htmlData = htmlDataMap.get(obj.html_id);
//         return {
//           ...obj,
//           name: htmlData.text,
//           siblings: htmlData.siblings
//         };
//       }

//       // Process all properties of the object
//       const updated = {};
//       for (const [key, value] of Object.entries(obj)) {
//         updated[key] = updateNestedObjects(value);
//       }
//       return updated;
//     };

//     // Start the recursive update
//     const result = updateNestedObjects(updatedData);

//     return result;
//   } catch (error:any) {
//     console.error('Error updating stored topics data:', error);
//     console.error('Error details:', {
//       message: error?.message,
//       stack: error?.stack
//     });
//     // return storedTopicsData;
//     throw error;
//   }
// };

// export const getTopicsPayloadForGiga = (topicsData = []) => {
//   try {
//     const arr = topicsData?.map((i:any) => {
//       const content = i?.TOPICS?.["page-level"]?.CONTENT?.filter((item) => item?.url !== "customer_url")
//         ?.map((item) => {
//           try {
//             const url = new URL(item?.url);
//             return {
//               competitor_domain: url.hostname,
//               html_tag: item?.html_tag,
//               inner_text: item?.name,
//             };
//           } catch (error) {
//             console.error("Invalid URL:", item?.url, error);
//             return null; // Skip invalid URLs
//           }
//         })
//         ?.filter(Boolean); // Remove null values

//       return {
//         TOPIC: i?.TOPIC || "Unknown",
//         COMPETITOR_CONTENT: content || [],
//         COMPETITOR_PREVALENCE: i?.TOPICS?.["page-level"]?.COMPETITOR_PREVALENCE || 0,
//         TOPIC_IMPORTANCE: i?.TOPICS?.["page-level"]?.TOPIC_IMPORTANCE || 0,
//         NUMBER_OF_SENTENCES_AT_OUR_PAGE_CURRENTLY: i?.TOPICS?.["page-level"]?.NUM_SENTS_CUSTOMER || 0,
//         NUMBER_OF_SENTENCES_AT_COMPETITOR_PAGES_CURRENTLY: i?.TOPICS?.["page-level"]?.NUM_SENTS_COMPETITORS || 0,
//       };
//     });

//     // Append 'of 47' to COMPETITOR_PREVALENCE safely
//     arr.forEach((item) => {
//       item.COMPETITOR_PREVALENCE = `${item.COMPETITOR_PREVALENCE} of 47`;
//     });

//     return arr;
//   } catch (error) {
//     console.error("Error in getTopicsPayloadForGiga:", error);
//     return [];
//   }
// };

export const getMappedTopicsContentAndImprovedContent = (storedTopicsData = [], storedPageAlHtmlData = []) => {
  try {
    if (!Array.isArray(storedTopicsData) || !Array.isArray(storedPageAlHtmlData)) {
      console.error('Invalid input: Expected arrays for storedTopicsData and storedPageAlHtmlData.');
      throw new Error('Invalid input: Expected arrays for storedTopicsData and storedPageAlHtmlData.');
    }

    // Create a map for quick lookup
    const htmlDataMap = new Map();
    storedPageAlHtmlData?.forEach((item: { id: string; text: string; siblings: any[] }) => {
      if (item?.id) {
        htmlDataMap.set(item.id, {
          text: item?.text || '',
          siblings: item?.siblings || [],
        });
      }
    });

    const updatedData = JSON.parse(JSON.stringify(storedTopicsData));
    if (htmlDataMap?.size === 0) {
      return updatedData;
    }

    // Recursive function to traverse and update all nested objects
    const updateNestedObjects = (obj) => {
      if (!obj || typeof obj !== 'object') return obj;

      if (Array.isArray(obj)) {
        return obj.map((item) => updateNestedObjects(item));
      }

      // Check if current object has html_id and update it
      const htmlId = obj?.html_id;
      if (htmlId && htmlDataMap?.has(htmlId)) {
        const htmlData = htmlDataMap?.get(htmlId);
        return {
          ...obj,
          name: htmlData?.text || obj?.name || '',
          siblings: htmlData?.siblings || obj?.siblings || [],
        };
      }

      // Process all properties of the object
      return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, updateNestedObjects(value)]));
    };

    // Start the recursive update
    const result = updateNestedObjects(updatedData);

    return result;
  } catch (error: any) {
    console.error('Error updating stored topics data:', {
      message: error?.message,
      stack: error?.stack,
    });
    throw new Error('Failed to map topics content. Please check the input data.');
  }
};

export const getTopicsPayloadForGiga = (topicsData = [], customerUrl, countOfCompetitorPrevalencePageLevel) => {
  try {
    if (!Array.isArray(topicsData)) {
      throw new Error('Invalid input: Expected arrays for topicsData.');
    }
    const arr = topicsData?.map((i: any) => {
      const competitorContent = i?.TOPICS?.['page-level']?.CONTENT?.filter((item) => item?.url !== customerUrl)
        ?.map((item) => {
          try {
            return {
              competitor_url: item?.url,
              html_tag: item?.html_tag,
              inner_text: item?.name,
              html_id: item?.html_id,
            };
          } catch (error) {
            console.error('Invalid URL:', item?.url, error);
            return null;
          }
        })
        ?.filter(Boolean);

      const customerContent = i?.TOPICS?.['page-level']?.CONTENT?.filter((item) => item?.url === customerUrl)
        ?.map((item) => {
          return {
            html_tag: item?.html_tag,
            inner_text: item?.name,
            siblings: item?.siblings,
          };
        })
        ?.filter(Boolean);

      return {
        TOPIC: i?.TOPIC || 'Unknown',
        TOPIC_ID: i?.TOPIC_ID || '',
        COMPETITOR_CONTENT: competitorContent || [],
        CUSTOMER_CONTENT: customerContent || [],
        COMPETITOR_PREVALENCE: i?.TOPICS?.['page-level']?.COMPETITOR_PREVALENCE || 0,
        TOPIC_IMPORTANCE: i?.TOPICS?.['page-level']?.TOPIC_IMPORTANCE || 0,
        NUMBER_OF_SENTENCES_AT_OUR_PAGE_CURRENTLY: i?.TOPICS?.['page-level']?.NUM_SENTS_CUSTOMER || 0,
        NUMBER_OF_SENTENCES_AT_COMPETITOR_PAGES_CURRENTLY: i?.TOPICS?.['page-level']?.NUM_SENTS_COMPETITORS || 0,
      };
    });

    arr.forEach((item) => {
      item.COMPETITOR_PREVALENCE = countOfCompetitorPrevalencePageLevel
        ? `${item.COMPETITOR_PREVALENCE} of ${countOfCompetitorPrevalencePageLevel}`
        : null;
    });

    return arr;
  } catch (error) {
    console.error('Error in getTopicsPayloadForGiga:', error);
    throw error;
  }
};

/**
 * Computes top competitor heading count and customer heading count from topics data.
 * Shared by giga_enhance_with_topics and FAQ generation payloads.
 *
 * @param topicsArray - storedTopicsData.TOPICS array
 * @param pageLevelData - storedTopicsData.contentQuality.CURRENT['page-level'] object
 * @param customerURL - the current page URL
 * @returns { topCompetitorHeadingCount, customerHeadingCount, topCompetitorUrl }
 */
export const getCompetitorHeadingCounts = (
  topicsArray: any[] = [],
  pageLevelData: Record<string, number> = {},
  customerURL = ''
) => {
  const topCompetitorUrl = Object.entries(pageLevelData)
    .filter(([url]) => url !== customerURL)
    .sort(([, scoreA], [, scoreB]) => (scoreB as number) - (scoreA as number))
    .map(([url]) => url)[0];

  const headingTags = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'];
  let topCompetitorHeadingCount = 0;
  let customerHeadingCount = 0;

  (topicsArray || []).forEach((topicEntry: any) => {
    if (
      topicEntry?.TOPICS?.['page-level']?.['CONTENT'] &&
      topicEntry?.TOPICS?.['page-level']?.['TOPIC_IMPORTANCE'] > 40
    ) {
      const contentItems = Array.isArray(topicEntry?.TOPICS?.['page-level']?.['CONTENT'])
        ? topicEntry?.TOPICS?.['page-level']?.['CONTENT'].flatMap((item: any) => (Array.isArray(item) ? item : [item]))
        : [topicEntry?.TOPICS?.['page-level']?.['CONTENT']];

      contentItems.forEach((contentItem: any) => {
        if (contentItem && Array.isArray(contentItem)) {
          contentItem.forEach((item: any) => {
            if (item?.html_tag && headingTags.includes(item.html_tag.toUpperCase())) {
              if (item.url === topCompetitorUrl) topCompetitorHeadingCount++;
              if (item.url === customerURL) customerHeadingCount++;
            }
          });
        } else if (contentItem?.html_tag && headingTags.includes(contentItem.html_tag.toUpperCase())) {
          if (contentItem.url === topCompetitorUrl) topCompetitorHeadingCount++;
          if (contentItem.url === customerURL) customerHeadingCount++;
        }
      });
    }
  });

  return { topCompetitorHeadingCount, customerHeadingCount, topCompetitorUrl };
};

/**
 * Builds targeted_keywords array from CIK keyword data (parent keywords only).
 * Shared by giga_enhance_with_topics and FAQ generation payloads.
 */
export const getTargetedKeywordsPayload = (storedCikKeywordData: any[] = []) => {
  const parentKeywords = (storedCikKeywordData || []).filter((kwd: any) => !kwd?.sub_keyword);
  return parentKeywords.map((kwd: any) => ({
    keyword: kwd?.keyword,
    keyword_importance: kwd?.keyword_importance || 100,
    relevance_rank: kwd?.relevance_rank || null,
  }));
};

type GigaVisibleKeywordSourceArgs = {
  storedKeywordsData?: any[];
  storedCikKeywordData?: any[];
  storedAiKeywordsData?: any[];
  isBrandedIncluded?: boolean;
};

const isTruthyKeywordFlag = (value: unknown) =>
  value === true ||
  value === 1 ||
  String(value || '')
    .trim()
    .toLowerCase() === 'true';

const isExplicitFalseKeywordFlag = (value: unknown) =>
  value === false ||
  value === 0 ||
  ['false', '0', 'no'].includes(
    String(value ?? '')
      .trim()
      .toLowerCase()
  );

export const isGigaSubKeywordRow = (kwd: any) =>
  Boolean(kwd?.sub_keyword) ||
  isTruthyKeywordFlag(kwd?.is_sub_keyword) ||
  Boolean(kwd?.['cr_insight_keywords.sub_keyword']);

const hasNonEmptyObject = (value: unknown) =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value as object).length);

const hasPositiveGigaScore = (value: unknown) => {
  const score = Number(value);
  return Number.isFinite(score) && score > 0;
};

export const hasGigaKeywordScoringEvidence = (kwd: any) =>
  hasPositiveGigaScore(kwd?.keyword_emphasis_projected) ||
  hasPositiveGigaScore(kwd?.keyword_emphasis) ||
  hasNonEmptyObject(kwd?.customer_html_tag_group_scores) ||
  hasNonEmptyObject(kwd?.competitor_html_tag_group_scores) ||
  (Array.isArray(kwd?.competitor_html_tag_group_scores) && kwd.competitor_html_tag_group_scores.length > 0);

const isGigaKeywordExplicitlyExcluded = (kwd: any) => isExplicitFalseKeywordFlag(kwd?.is_included);

const dedupeGigaKeywordRows = (rows: any[] = [], isBrandedIncluded = true) => {
  const seen = new Set<string>();
  return (Array.isArray(rows) ? rows : [])
    .filter((kwd: any) => kwd?.keyword && !isGigaSubKeywordRow(kwd))
    .filter((kwd: any) => !isGigaKeywordExplicitlyExcluded(kwd))
    .filter((kwd: any) => isBrandedIncluded || !kwd?.is_branded)
    .filter((kwd: any) => {
      const key = String(kwd.keyword || '')
        .trim()
        .toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

export const getVisibleGigaParentKeywords = ({
  storedKeywordsData = [],
  storedCikKeywordData = [],
  storedAiKeywordsData = [],
  isBrandedIncluded = true,
}: GigaVisibleKeywordSourceArgs = {}) => {
  const visibleKeywordRows = dedupeGigaKeywordRows(storedKeywordsData, isBrandedIncluded).filter(
    hasGigaKeywordScoringEvidence
  );
  if (visibleKeywordRows.length) return visibleKeywordRows;

  // If merged keyword rows are present but lost their score fields, fall back
  // to scored CIK rows rather than sending an unscored raw keyword universe.
  const fallbackCikRows = dedupeGigaKeywordRows(storedCikKeywordData, isBrandedIncluded).filter(
    hasGigaKeywordScoringEvidence
  );
  if (fallbackCikRows.length) return fallbackCikRows;

  return dedupeGigaKeywordRows(
    (Array.isArray(storedAiKeywordsData) ? storedAiKeywordsData : []).filter((kwd: any) => kwd?.is_included),
    isBrandedIncluded
  );
};

const normalizeKeywordPayloadKey = (keyword: unknown) =>
  String(keyword || '')
    .trim()
    .toLowerCase();

/**
 * Builds the GIGA keywords payload from rows that have already been filtered to
 * the same parent keywords visible in the Page Workflow keyword table.
 */
export const getGigaKeywordPayload = (keywordRows: any[] = []) => {
  return (Array.isArray(keywordRows) ? keywordRows : [])
    .filter((kwd: any) => kwd?.keyword)
    .map((kwd: any) => ({
      keyword: kwd?.keyword,
      keyword_importance: kwd?.keyword_importance || 100,
      keyword_emphasis: isValidScore(kwd?.keyword_emphasis_projected)
        ? kwd?.keyword_emphasis_projected
        : kwd?.keyword_emphasis,
      is_branded: kwd?.is_branded,
    }));
};

/**
 * Builds targeted_keywords from pre-filtered visible parent rows. The optional
 * rank lookup preserves the formatted "Relevance Rank" value used by the
 * visible table, while keeping old raw-row fallback behavior for legacy callers.
 */
export const getGigaTargetedKeywordsPayload = (
  keywordRows: any[] = [],
  relevanceRankByKeyword: Record<string, any> = {}
) => {
  return (Array.isArray(keywordRows) ? keywordRows : [])
    .filter((kwd: any) => kwd?.keyword)
    .map((kwd: any) => {
      const key = normalizeKeywordPayloadKey(kwd?.keyword);
      return {
        keyword: kwd?.keyword,
        keyword_importance: kwd?.keyword_importance || 100,
        relevance_rank: relevanceRankByKeyword?.[key] ?? kwd?.relevance_rank ?? null,
      };
    });
};

const finiteGigaScoreOrLowest = (value: unknown) => {
  const score = Number(value);
  return Number.isFinite(score) ? score : Number.NEGATIVE_INFINITY;
};

export const normalizeGigaCompetitorUrlKey = (value: unknown) =>
  String(value || '')
    .trim()
    .replace(/\/+$/, '')
    .toLowerCase();

const GIGA_KSM_TAG_GROUP_ALIASES: Record<string, string> = {
  headings: 'headings',
  heading: 'headings',
  h1: 'headings',
  h2: 'headings',
  h3: 'headings',
  h4: 'headings',
  h5: 'headings',
  h6: 'headings',
  meta: 'meta',
  metadata: 'meta',
  'meta data': 'meta',
  title: 'meta',
  description: 'meta',
  'meta description': 'meta',
  meta_description: 'meta',
  body: 'body',
  passage: 'body',
  passages: 'body',
  images: 'images',
  image: 'images',
  img: 'images',
  alt: 'images',
  'alt text': 'images',
  alt_text: 'images',
  links: 'links',
  link: 'links',
  anchor: 'links',
  'anchor text': 'links',
  anchor_text: 'links',
  link_anchor: 'links',
  'link-anchor': 'links',
  'structured data': 'structured data',
  structured_data: 'structured data',
  'structured-data': 'structured data',
  structureddata: 'structured data',
  jsonld: 'structured data',
  'json-ld': 'structured data',
};

const normalizeGigaKsmTagGroupKey = (raw: unknown) => {
  const key = String(raw || '')
    .toLowerCase()
    .trim();
  return GIGA_KSM_TAG_GROUP_ALIASES[key] || null;
};

export const countKrDisplayedChangesFromElements = (elementsById: Record<string, any> | undefined | null): number => {
  const entries = Object.values(elementsById || {}).filter(
    (entry: any) => entry?.accepted && !entry?.rejected && !entry?.internalOnly
  );
  return entries.reduce((total, entry: any) => {
    const tagGroup =
      normalizeGigaKsmTagGroupKey(entry?.tagGroup) ||
      String(entry?.tagGroup || '')
        .toLowerCase()
        .trim();
    if (!tagGroup) return total;
    const baseCount = 1;
    const sectionParagraphs = Array.isArray(entry?.sectionPayload?.paragraphs)
      ? entry.sectionPayload.paragraphs.filter((paragraph: any) => String(paragraph || '').trim())
      : [];
    const hasSeparatePassageChange =
      entry?.operationType === 'section-add' && tagGroup !== 'body' && sectionParagraphs.length > 0;
    return total + baseCount + (hasSeparatePassageChange ? 1 : 0);
  }, 0);
};

const mergeBestKnownTagGroupScore = (target: Record<string, number>, rawKey: unknown, rawValue: unknown) => {
  const tagGroup = normalizeGigaKsmTagGroupKey(rawKey);
  if (!tagGroup || !isValidScore(rawValue)) return;
  const score = Number(rawValue) || 0;
  if (score > (target[tagGroup] ?? 0)) target[tagGroup] = score;
};

export const extractGigaCompetitorTagGroupScores = (scoreRow: any) => {
  const out: Record<string, number> = {};
  if (!scoreRow || typeof scoreRow !== 'object') return out;

  const nested = scoreRow.tag_group_scores;
  if (nested && typeof nested === 'object') {
    for (const key of Object.keys(nested)) {
      if (nested[key] && typeof nested[key] === 'object') {
        for (const group of Object.keys(nested[key])) {
          mergeBestKnownTagGroupScore(out, group, nested[key][group]);
        }
      } else {
        mergeBestKnownTagGroupScore(out, key, nested[key]);
      }
    }
  }

  for (const key of Object.keys(scoreRow)) {
    if (['tag_group_scores', 'competitor_page', 'url', 'name'].includes(key)) continue;
    mergeBestKnownTagGroupScore(out, key, scoreRow[key]);
  }

  return out;
};

const scoreRowFromMaybeCompetitorMapValue = (value: any) => {
  if (!value || typeof value !== 'object') return null;
  return value.tag_group_scores ? value : { tag_group_scores: value };
};

export const findGigaCompetitorTagGroupScoreRow = (scores: any, competitorKey: string) => {
  if (!scores || !competitorKey) return null;

  if (!Array.isArray(scores) && typeof scores === 'object') {
    const directValue = scores[competitorKey];
    if (directValue && typeof directValue === 'object') {
      return scoreRowFromMaybeCompetitorMapValue(directValue);
    }
    for (const key of Object.keys(scores)) {
      if (normalizeGigaCompetitorUrlKey(key) !== competitorKey) continue;
      return scoreRowFromMaybeCompetitorMapValue(scores[key]);
    }
  }

  const rows = Array.isArray(scores) ? scores : scores && typeof scores === 'object' ? [scores] : [];
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const rowUrl = normalizeGigaCompetitorUrlKey(row.competitor_page || row.url || row.name);
    if (rowUrl && rowUrl === competitorKey) return row;

    const nested = row.tag_group_scores;
    if (!nested || typeof nested !== 'object') continue;
    for (const key of Object.keys(nested)) {
      if (normalizeGigaCompetitorUrlKey(key) !== competitorKey) continue;
      return scoreRowFromMaybeCompetitorMapValue(nested[key]);
    }
  }
  return null;
};

const mergeCompetitorScoreMapValue = (
  target: Record<string, number>,
  value: any,
  selected: Set<string>,
  rawKey?: string
) => {
  const key = normalizeGigaCompetitorUrlKey(rawKey);
  if (selected.size && key && !selected.has(key)) return;
  const scores = extractGigaCompetitorTagGroupScores(scoreRowFromMaybeCompetitorMapValue(value));
  for (const group of Object.keys(scores)) {
    mergeBestKnownTagGroupScore(target, group, scores[group]);
  }
};

const looksLikeGigaCompetitorScoreMapEntry = (rawKey: unknown, value: any, allowNameOnlyKey = false) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  if (normalizeGigaKsmTagGroupKey(rawKey)) return false;

  const normalizedKey = normalizeGigaCompetitorUrlKey(rawKey);
  const keyLooksUrlish = /^https?:\/\//.test(normalizedKey) || normalizedKey.includes('.');
  if (value.tag_group_scores || value.competitor_page || value.url || value.name) return true;
  if (!keyLooksUrlish && !allowNameOnlyKey) return false;
  return Object.keys(value).some((key) => Boolean(normalizeGigaKsmTagGroupKey(key)));
};

export const aggregateGigaCompetitorTagGroupScores = (scores: any, selectedCompetitorUrls: unknown[] = []) => {
  const selected = new Set((selectedCompetitorUrls || []).map(normalizeGigaCompetitorUrlKey).filter(Boolean));
  const best: Record<string, number> = {};

  if (!scores) return best;

  if (!Array.isArray(scores) && typeof scores === 'object') {
    if (scores.tag_group_scores || scores.competitor_page || scores.url || scores.name) {
      const competitorKey = normalizeGigaCompetitorUrlKey(scores.competitor_page || scores.url || scores.name);
      if (!selected.size || !competitorKey || selected.has(competitorKey)) {
        return extractGigaCompetitorTagGroupScores(scores);
      }
      return best;
    }
    const keys = Object.keys(scores);
    const competitorMapKeys = keys.filter((key) =>
      looksLikeGigaCompetitorScoreMapEntry(key, scores[key], !selected.size)
    );
    if (competitorMapKeys.length) {
      competitorMapKeys.forEach((key) => mergeCompetitorScoreMapValue(best, scores[key], selected, key));
      if (!selected.size) {
        keys.forEach((key) => mergeBestKnownTagGroupScore(best, key, scores[key]));
      }
      return best;
    }
    return extractGigaCompetitorTagGroupScores(scores);
  }

  if (!Array.isArray(scores)) return best;

  for (const comp of scores) {
    if (!comp || typeof comp !== 'object') continue;
    const competitorUrl = comp.competitor_page || comp.url || comp.name;
    const competitorKey = normalizeGigaCompetitorUrlKey(competitorUrl);
    if (selected.size && competitorKey && !selected.has(competitorKey)) continue;

    const extracted = extractGigaCompetitorTagGroupScores(comp);
    for (const group of Object.keys(extracted)) {
      mergeBestKnownTagGroupScore(best, group, extracted[group]);
    }
  }

  return best;
};

export const sortGigaCompetitorMetadataExamplesByScore = (examples: any[] = []) => {
  return [...(Array.isArray(examples) ? examples : [])].sort(
    (a: any, b: any) => finiteGigaScoreOrLowest(b?.metaScore) - finiteGigaScoreOrLowest(a?.metaScore)
  );
};

const normalizeGigaMetadataExampleText = (value: unknown) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

const getGigaMetadataExampleText = (item: any) =>
  normalizeGigaMetadataExampleText(
    item?.text ||
      item?.corpus ||
      item?.inner_text ||
      item?.element_text ||
      item?.html_text ||
      item?.content ||
      item?.value ||
      item?.name
  );

const metadataExampleKeyForTag = (rawTag: unknown) => {
  const tag = String(rawTag || '')
    .toLowerCase()
    .trim();
  if (tag === 'title') return 'title';
  if (tag === 'h1') return 'h1';
  if (['description', 'meta-desc', 'meta_desc', 'meta description'].includes(tag)) return 'description';
  return null;
};

export const extractGigaCompetitorMetadataExamples = (keywordData: any, selectedCompetitorUrls: unknown[] = []) => {
  const selected = new Set((selectedCompetitorUrls || []).map(normalizeGigaCompetitorUrlKey).filter(Boolean));
  const competitors = Array.isArray(keywordData?.competitor_html_element_scores)
    ? keywordData.competitor_html_element_scores
    : [];

  const examples = competitors
    .filter((comp: any) => {
      const competitorPage = comp?.competitor_page || comp?.url || comp?.name;
      return !selected.size || selected.has(normalizeGigaCompetitorUrlKey(competitorPage));
    })
    .map((comp: any) => {
      const competitorPage = comp?.competitor_page || comp?.url || comp?.name;
      const competitorKey = normalizeGigaCompetitorUrlKey(competitorPage);
      const tagScore = findGigaCompetitorTagGroupScoreRow(keywordData?.competitor_html_tag_group_scores, competitorKey);
      const tagGroupScores = extractGigaCompetitorTagGroupScores(tagScore);
      const example: any = {
        keyword: keywordData?.keyword,
        competitorPage,
        metaScore: tagGroupScores.meta,
      };
      if (Object.keys(tagGroupScores).length) example.tagGroupScores = tagGroupScores;

      const elementScores = Array.isArray(comp?.scores)
        ? comp.scores
        : Array.isArray(comp?.elements)
        ? comp.elements
        : [];
      elementScores.forEach((item: any) => {
        const key = metadataExampleKeyForTag(item?.element_tag || item?.tag || item?.html_tag || item?.tagName);
        if (!key || example[key]) return;
        const text = getGigaMetadataExampleText(item);
        if (text) example[key] = text;
      });

      ['title', 'h1', 'description'].forEach((key) => {
        if (!example[key]) {
          const text = normalizeGigaMetadataExampleText(comp?.[key] || comp?.[`meta_${key}`]);
          if (text) example[key] = text;
        }
      });

      return example;
    })
    .filter((example: any) => example.title || example.h1 || example.description);

  return sortGigaCompetitorMetadataExamplesByScore(examples).slice(0, 4);
};

const headingOutlineTagForTag = (rawTag: unknown) => {
  const tag = String(rawTag || '')
    .toUpperCase()
    .trim();
  return ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(tag) ? tag : null;
};

const readGigaHeadingOutlineOrder = (item: any, key: string) => {
  const explicit = Number(item?.elementOrderNumber ?? item?.element_order_number);
  if (Number.isFinite(explicit)) return explicit;
  const sequence = Number(item?.sequenceNumber ?? item?.sequence_number);
  if (Number.isFinite(sequence)) return sequence;
  const tagOrder = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].indexOf(key.toLowerCase());
  return tagOrder >= 0 ? (tagOrder + 1) * 100000 : Number.POSITIVE_INFINITY;
};

const normalizeGigaHeadingOutlineNodes = (raw: any, tag: string) => {
  const tagName = headingOutlineTagForTag(tag);
  if (!tagName) return [];
  const items = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? [raw] : [{ text: raw }];
  return items
    .map((item: any, index: number) => {
      const text = normalizeGigaMetadataExampleText(
        item?.text ||
          item?.inner_text ||
          item?.element_text ||
          item?.html_text ||
          item?.content ||
          item?.value ||
          item?.name ||
          (typeof item === 'string' ? item : '')
      );
      if (!text) return null;
      const elementOrderNumber = Number(item?.elementOrderNumber ?? item?.element_order_number);
      const sequenceNumber = Number(item?.sequenceNumber ?? item?.sequence_number);
      return {
        tag: tagName,
        text,
        ...(item?.id != null ? { id: item.id } : {}),
        ...(Number.isFinite(elementOrderNumber) ? { elementOrderNumber } : {}),
        ...(Number.isFinite(sequenceNumber) ? { sequenceNumber } : {}),
        __sortOrder: Number.isFinite(elementOrderNumber)
          ? elementOrderNumber
          : Number.isFinite(sequenceNumber)
          ? sequenceNumber
          : readGigaHeadingOutlineOrder(item, tag) + index,
      };
    })
    .filter(Boolean);
};

export const extractGigaCompetitorHeadingOutlines = (keywordData: any, selectedCompetitorUrls: unknown[] = []) => {
  const selected = new Set((selectedCompetitorUrls || []).map(normalizeGigaCompetitorUrlKey).filter(Boolean));
  const competitors = Array.isArray(keywordData?.competitor_html_element_scores)
    ? keywordData.competitor_html_element_scores
    : [];

  const outlines = competitors
    .filter((comp: any) => {
      const competitorPage = comp?.competitor_page || comp?.url || comp?.name;
      return !selected.size || selected.has(normalizeGigaCompetitorUrlKey(competitorPage));
    })
    .map((comp: any) => {
      const competitorPage = comp?.competitor_page || comp?.url || comp?.name;
      const competitorKey = normalizeGigaCompetitorUrlKey(competitorPage);
      const tagScore = findGigaCompetitorTagGroupScoreRow(keywordData?.competitor_html_tag_group_scores, competitorKey);
      const tagGroupScores = extractGigaCompetitorTagGroupScores(tagScore);
      const elementScores = Array.isArray(comp?.scores)
        ? comp.scores
        : Array.isArray(comp?.elements)
        ? comp.elements
        : [];

      const headingNodes: any[] = [];
      elementScores.forEach((item: any) => {
        const tag = headingOutlineTagForTag(item?.element_tag || item?.tag || item?.html_tag || item?.tagName);
        if (!tag) return;
        headingNodes.push(...normalizeGigaHeadingOutlineNodes(item, tag));
      });
      ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].forEach((key) => {
        if (comp?.[key] == null) return;
        headingNodes.push(...normalizeGigaHeadingOutlineNodes(comp[key], key));
      });

      const seen = new Set<string>();
      const headings = headingNodes
        .sort((a, b) => Number(a.__sortOrder) - Number(b.__sortOrder))
        .filter((node) => {
          const dedupeKey = `${node.tag}:${normalizeGigaMetadataExampleText(node.text).toLowerCase()}`;
          if (seen.has(dedupeKey)) return false;
          seen.add(dedupeKey);
          return true;
        })
        .slice(0, 80)
        .map(({ __sortOrder, ...node }) => node);

      return {
        keyword: keywordData?.keyword,
        competitorPage,
        headings,
        headingScore: tagGroupScores.headings,
        ...(Object.keys(tagGroupScores).length ? { tagGroupScores } : {}),
        source: 'competitor_html_element_scores',
      };
    })
    .filter((outline: any) => outline.competitorPage && outline.headings?.length);

  return [...outlines]
    .sort((a: any, b: any) => finiteGigaScoreOrLowest(b?.headingScore) - finiteGigaScoreOrLowest(a?.headingScore))
    .slice(0, 4);
};

export const extractGigaCompetitorTagGroupScoresByCompetitor = (
  keywordData: any,
  selectedCompetitorUrls: unknown[] = []
) => {
  const out: Record<string, Record<string, number>> = {};
  const selectedUrls = (selectedCompetitorUrls || []).map((url) => String(url || '').trim()).filter(Boolean);
  const candidateUrls = selectedUrls.length
    ? selectedUrls
    : Array.isArray(keywordData?.competitor_html_element_scores)
    ? keywordData.competitor_html_element_scores
        .map((comp: any) => comp?.competitor_page || comp?.url || comp?.name)
        .filter(Boolean)
    : [];

  candidateUrls.forEach((url) => {
    const competitorKey = normalizeGigaCompetitorUrlKey(url);
    const tagScore = findGigaCompetitorTagGroupScoreRow(keywordData?.competitor_html_tag_group_scores, competitorKey);
    const tagGroupScores = extractGigaCompetitorTagGroupScores(tagScore);
    if (Object.keys(tagGroupScores).length) {
      out[competitorKey] = tagGroupScores;
    }
  });

  return out;
};

export const getTagGroupKeyByTagGroupTag = (selectedTagGroupTabName) => {
  switch (selectedTagGroupTabName) {
    case CR_TAB_KEYS.SUB_EDITOR_TABS.HEADINGS:
      return 'headings';
    case CR_TAB_KEYS.SUB_EDITOR_TABS.META:
      return 'meta';
    case CR_TAB_KEYS.SUB_EDITOR_TABS.BODY:
      return 'body';
    case CR_TAB_KEYS.SUB_EDITOR_TABS.ANCHOR_TEXT:
      return 'links';
    case CR_TAB_KEYS.SUB_EDITOR_TABS.ALT_TEXT:
      return 'images';
    case CR_TAB_KEYS.SUB_EDITOR_TABS.STRUCTURE_DATA:
      return 'structured_data';
    default:
      return null;
  }
};

export const getNumOfSentLine = (customerNum, compNumOfSents) => {
  const hasCustomer =
    customerNum !== null && customerNum !== undefined && customerNum !== '' && Number(customerNum) > 0;
  const hasComp = compNumOfSents !== null && compNumOfSents !== undefined && compNumOfSents !== '';

  if (!hasCustomer && !hasComp) return '';

  let line = '';

  if (hasCustomer) {
    const isRange = String(customerNum).includes('-');
    const suffix = !isRange && Number(customerNum) === 1 ? 'Sentence' : 'Sentences';
    line = `You: ${customerNum} ${suffix}`;
  }

  if (hasComp) {
    const isRange = String(compNumOfSents).includes('-');
    const suffix = !isRange && Number(compNumOfSents) === 1 ? 'Sentence' : 'Sentences';
    line = hasCustomer ? `${line}, Competitor: ${compNumOfSents} ${suffix}` : `Competitor: ${compNumOfSents} ${suffix}`;
  }

  return line.trim();
};

export const sanitizeFileName = (slug) => {
  return slug
    .replace(/\//g, '_') // replace slashes
    .replace(/[\?\&\=\%\#]/g, '-') // replace query symbols
    .replace(/\s+/g, '_') // replace spaces
    .replace(/[^a-zA-Z0-9_\-]/g, '') // remove other invalid chars
    .toLowerCase();
};

export const cleanSlug = (url) => {
  try {
    const parsed = new URL(url);

    // get path, remove leading/trailing slash
    let path = parsed.pathname.replace(/^\/|\/$/g, '');

    // use 'home' if path is empty
    if (!path) path = 'home';

    // append query and hash
    if (parsed.search) path += '-' + parsed.search.slice(1); // remove '?'
    if (parsed.hash) path += '-' + parsed.hash.slice(1); // remove '#'

    // sanitize: replace slashes and invalid filename chars
    path = path
      .replace(/\//g, '_')
      .replace(/[\?\&\=\%\#]/g, '-') // already handled, but safe
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_\-]/g, ''); // final cleanup
    path = path.replace(/\..*$/, ''); // Remove dot and everything after it (like .html)
    return path;
  } catch (e) {
    console.error('Invalid URL', e);
    return '';
  }
};

export const isTextIsValid = (str) => {
  const cleaned = str?.replace(/[\u200B-\u200D\uFEFF]/g, '')?.trim();
  return cleaned?.length === 0 ? false : true;
};

// export const countSentences = (text) => {
//   return text.split(/[.!?:]+/).filter(Boolean).length;
// };
export const countSentences = (text) => {
  return text.split(/(?<=[.!?])\s+/).filter((s) => s.trim().length > 0).length;
};

export const hasInnerTag = ({ tag, eleId, content }) => {
  const regex = new RegExp(`<${tag}[^>]*?element_id=["']${eleId}["'][^>]*>([\\s\\S]*?)<\\/${tag}>`, 'i');

  const match = (content || '')?.match(regex);
  const innerContent = match?.[1] || '';
  const hasNestedTag = /<\s*([a-zA-Z0-9]+)(?:\s[^>]*)?>/.test(innerContent);
  return { hasNestedTag, extractedHtml: match?.[0] };
};
export const sortTagGroupElements = (elements, sortBy, extractedIds = []) => {
  return [...elements].sort((a, b) => {
    const getDate = (el) => new Date(el.updated_on || el.created_on || '').getTime() || null;

    switch (sortBy) {
      case 'score_desc': {
        const aScore = a.projectedHtmlElementScore ?? a.htmlElementScore;
        const bScore = b.projectedHtmlElementScore ?? b.htmlElementScore;

        // Push null/undefined to bottom
        if (aScore == null && bScore == null) return 0;
        if (aScore == null) return 1;
        if (bScore == null) return -1;

        return Number(bScore) - Number(aScore); // highest first
      }
      case 'score_asc': {
        const aScore = a.projectedHtmlElementScore ?? a.htmlElementScore;
        const bScore = b.projectedHtmlElementScore ?? b.htmlElementScore;

        // Push null/undefined to bottom
        if (aScore == null && bScore == null) return 0;
        if (aScore == null) return 1;
        if (bScore == null) return -1;

        return Number(aScore) - Number(bScore); // lowest first
      }
      case 'proposed_desc': {
        const aDate = getDate(a);
        const bDate = getDate(b);

        if (a.isSuggested && !b.isSuggested) return -1;
        if (!a.isSuggested && b.isSuggested) return 1;

        // Push null dates to bottom
        if (aDate == null && bDate == null) return 0;
        if (aDate == null) return 1;
        if (bDate == null) return -1;

        return bDate - aDate; // latest first
      }
      case 'improved_desc': {
        const aDate = getDate(a);
        const bDate = getDate(b);

        if (!a.isSuggested && b.isSuggested) return -1;
        if (a.isSuggested && !b.isSuggested) return 1;

        // Push null dates to bottom
        if (aDate == null && bDate == null) return 0;
        if (aDate == null) return 1;
        if (bDate == null) return -1;

        return bDate - aDate; // latest first
      }
      case 'page_wise': {
        if (a.elementOrderNumber && b?.elementOrderNumber && !extractedIds?.length) {
          return a.elementOrderNumber - b.elementOrderNumber;
        } else {
          const existingIds = [...(extractedIds || [])]?.map((id) => Number(id));
          const aIndex = existingIds?.length > 0 ? existingIds.indexOf(a?.id) : -1;
          const bIndex = existingIds?.length > 0 ? existingIds.indexOf(b?.id) : -1;

          // Elements not found in existingIds go to the bottom
          if (aIndex === -1 && bIndex === -1) return 0;
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;

          return aIndex - bIndex; // respect existingIds order
        }
      }
      default:
        return 0;
    }
  });
};

export const liveUrlValidator = async (url) => {
  try {
    const apiResponse = await ApiService.post(
      `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/recommendations/get-url-live-status`,
      {
        url: url,
      }
    );
    return apiResponse?.data?.url_details?.islive == true;
  } catch (error) {
    console.error('Error:', error);
    return false;
  }
};

export const cleanHtmlAllowSomeAttributesAndConvertHeadings = (html) => {
  // 0️⃣ Remove TinyMCE comments
  html = html.replace(/<!--[\s\S]*?-->/g, '');

  const div = document.createElement('div');
  div.innerHTML = html;

  const allowedAttributes = ['href', 'src', 'alt', 'title'];

  const walker = document.createTreeWalker(div, NodeFilter.SHOW_ELEMENT, null);
  let node;

  while ((node = walker.nextNode())) {
    const tagName = node.tagName.toLowerCase();

    // 1️⃣ Remove <img> tags
    if (tagName === 'img') {
      node.remove();
      continue;
    }

    // 2️⃣ Convert H1-H6 → P (plain text)
    if (/^h[1-6]$/.test(tagName)) {
      const p = document.createElement('p');
      p.textContent = node.textContent.trim(); // plain text
      node.replaceWith(p);
      node = p;
    }

    // 3️⃣ Remove attributes except allowed
    for (const attr of [...node.attributes].map((a) => a.name)) {
      if (!allowedAttributes.includes(attr)) {
        node.removeAttribute(attr);
      }
    }
  }

  // 4️⃣ Remove empty tags (after transformations)
  removeEmptyNodes(div);

  return div.innerHTML;
};

// Helper function to recursively remove empty elements
function removeEmptyNodes(root) {
  const all = root.querySelectorAll('*');

  all.forEach((el) => {
    const text = el.textContent.trim();

    const hasOnlyWhitespace = text.length === 0;
    const hasNoChildren = el.children.length === 0;

    // Remove if:
    // - No text
    // - No children
    if (hasOnlyWhitespace && hasNoChildren) {
      el.remove();
    }
  });
}

export function removeEmptyElementsWithId(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Select all elements that contain element_id attribute
  const elements = doc.querySelectorAll('[element_id]');

  // Void elements (self-closing) should never be considered "empty"
  const voidElements = [
    'img',
    'br',
    'hr',
    'input',
    'meta',
    'link',
    'area',
    'base',
    'col',
    'embed',
    'source',
    'track',
    'wbr',
  ];

  elements.forEach((el) => {
    const tagName = el.tagName?.toLowerCase();

    // Skip void elements - they are inherently empty but not "empty content"
    if (voidElements.includes(tagName)) {
      return;
    }

    const innerHTMLTrim = el?.innerHTML?.trim() || '';
    const textTrim = el?.textContent?.trim() || '';

    // remove empty: no innerText + only <br> allowed
    const isEmpty = (innerHTMLTrim === '<br>' || innerHTMLTrim === '') && textTrim === '';

    if (isEmpty) {
      el.remove();
    }
  });

  return doc.body.innerHTML;
}

/**
 * Calculate how many new clusters we may target now.
 *
 * @param {number} pendingClustersToTarget - slots left to reach TOTAL_LIMIT (i.e. TOTAL_LIMIT - alreadyTargetedTotal)
 * @param {object} opts
 * @param {number} opts.totalLimit - overall allowed targets (default 10)
 * @param {number} opts.manualCap - max allowed from manual/discover flow (default 6)
 * @returns {{ alreadyTargetedCount: number, maxNewTargetAllowed: number }}
 */
export function calculateAllowedTargets(pendingClustersToTarget, { totalLimit = 10, manualCap = 6 } = {}) {
  // normalize inputs
  const pending = Math.max(0, Math.floor(Number(pendingClustersToTarget) || 0));
  const TOTAL = Math.max(1, Math.floor(Number(totalLimit)));
  const MANUAL = Math.max(0, Math.floor(Number(manualCap)));

  // how many are already targeted in total
  const alreadyTargetedCount = Math.min(TOTAL, Math.max(0, TOTAL - pending));

  // how many more we can add from manual/discover considering the MANUAL cap
  // i.e. we should not add more than (MANUAL - alreadyTargetedCount) from manual flow
  const manualRemainingCap = Math.max(0, MANUAL - alreadyTargetedCount);

  // overall we also cannot exceed pending (the actual free slots)
  const maxNewTargetAllowed = Math.min(pending, manualRemainingCap);

  return {
    alreadyTargetedCount,
    maxNewTargetAllowed,
  };
}

export function extractS3Images(htmlString) {
  const container = document.createElement('div');
  container.innerHTML = htmlString;

  const allImages = Array.from(container.querySelectorAll('img'));

  const s3Images = allImages
    .map((img) => img.getAttribute('src'))
    .filter((src) => src?.startsWith('https://qwa-public-assets.s3'));

  return s3Images;
}

export async function fetchImageAsBlob(url) {
  try {
    // Add cache-busting parameter to avoid cached non-CORS responses
    const cacheBustUrl = url + (url.includes('?') ? '&' : '?') + '_t=' + Date.now();
    const response = await fetch(cacheBustUrl, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-store',
    });
    if (response.ok) {
      return await response.blob();
    }
    return null;
  } catch (error) {
    console.error('Failed to fetch image:', error);
    return null;
  }
}

export const derivedCmsSlug = (url, pagesPath) => {
  if (!url) return null;

  // Normalize pagesPath: strip leading/trailing slashes
  const normalizedPrefix = pagesPath != null ? String(pagesPath).replace(/^\/+|\/+$/g, '') : null;

  try {
    const { pathname } = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');

    const slug = pathname.replace(/^\/+|\/+$/g, ''); // remove leading/trailing slash

    // If pagesPath provided, strip that prefix
    if (normalizedPrefix) {
      if (slug.startsWith(normalizedPrefix + '/')) {
        return slug.slice(normalizedPrefix.length + 1) || null;
      }
    }

    // Backward compat: when pagesPath not provided, fall back to blog/
    if (normalizedPrefix == null && slug.startsWith('blog/')) {
      return slug.replace(/^blog\//, '');
    }

    // Otherwise return normal slug
    return slug || null;
  } catch {
    // Fallback for non-URL strings
    let s = String(url).replace(/^\/+|\/+$/g, '');

    if (normalizedPrefix) {
      if (s.startsWith(normalizedPrefix + '/')) {
        return s.slice(normalizedPrefix.length + 1) || null;
      }
    }

    if (normalizedPrefix == null && s.startsWith('blog/')) {
      return s.replace(/^blog\//, '');
    }

    // Remove domain if included in raw string
    s = s.replace(/^https?:\/\/[^/]+\//, '').replace(/^\/+|\/+$/g, '');

    return s || null;
  }
};

/**
 * Build a CMS page URL from domain, slug, and optional pagesPath.
 * Does NOT auto-prepend pagesPath — the slug is used as-is.
 * Only deduplicates when domain already ends with pagesPath and slug also starts with it.
 */
export const buildCmsPageUrl = (domain, slug, pagesPath) => {
  if (!domain) return slug || '';

  // Normalize: remove trailing slash from domain
  const base = domain.replace(/\/+$/, '');

  if (!slug) return base;

  let cleanSlug = slug.replace(/^\/+/, '');

  // Dedup: if domain already ends with pagesPath and slug starts with same prefix, strip it from slug
  const normalizedPrefix = pagesPath ? String(pagesPath).replace(/^\/+|\/+$/g, '') : '';
  if (normalizedPrefix && base.endsWith('/' + normalizedPrefix) && cleanSlug.startsWith(normalizedPrefix + '/')) {
    cleanSlug = cleanSlug.slice(normalizedPrefix.length + 1);
  }

  return base + '/' + cleanSlug;
};
